import { useEffect, useRef, useState } from "react";
import type { Dispatch, ReactElement, ReactNode, RefObject,
              SetStateAction } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { Abrechnungsquellen, ApiClient, Auftrag, Ansprechpartner, Benutzer, InvoiceFromOrderPreview,
              KonditionAuswahl, PositionsStand, Rechnung, Unternehmen } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload,
         useBestaetigung, useFormularZustand } from "@kundeninfo/ui";
import { Auswahl, Chip, Formular, Hinweis, Schalter, quittung } from "@kundeninfo/ui/me-design";
import type { FormularElement } from "@kundeninfo/ui/me-design";
import { VORGANG_BESCHRIFTUNGEN, draftSetter, folgewegFehler } from "./AngebotDetail.js";
import { laufzeitPaar } from "./laufzeitPaar.js";
import { Belegvorschau } from "./Belegvorschau.js";
import { PositionsEditor, laufzeitVorschauWerte } from "./PositionsEditor.js";
import type { PositionsEditorClient, PositionAssignment } from "./PositionsEditor.js";
import { Vorgangsabschnitt } from "./Vorgangsabschnitt.js";
import { useSectionFolds, showFieldIn } from "./vorgangsabschnitt/sectionFolds.js";
import type { SectionFields, SectionFolds } from "./vorgangsabschnitt/sectionFolds.js";
import { SECTION_TEXT } from "./vorgangsabschnitt/texts.js";
import { partiesSummary, ratesSummary, termSummary } from "./vorgangsabschnitt/summaries.js";
import { FilesReadSection, useJumpAfterLeaving, counterProp, filesContent, findingsPerSection, hasText, itemsHead,
         openSectionsWithFindings, sectionRenderer, showFindingIn, summaryProp,
         useLoadedState } from "./vorgangsabschnitt/sections.js";
import type { FilesSource, SectionContent } from "./vorgangsabschnitt/sections.js";
import type { DocumentFilesClient } from "./Vorgangsdateien.js";
import { ROW_TEXT } from "../views/customerOverview/texts.js";
import { heuteAlsTag } from "../format.js";
import { BelegParteien, PARTEIEN_KOPFFELDER } from "./BelegParteien.js";
import type { BelegParteienModalClient } from "./BelegParteien.js";
import { BelegLayoutKnopf } from "./BelegLayout.js";
import type { BelegLayoutClient } from "./BelegLayout.js";
import { Zahlplan, ZAHLPLAN_KOPFFELDER } from "./Zahlplan.js";
import type { ZahlplanModalClient } from "./Zahlplan.js";
import { GeplanteFaelligkeiten } from "./GeplanteFaelligkeiten.js";
import type { FaelligkeitenClient } from "./GeplanteFaelligkeiten.js";
import { Rechnungsvorschlag, defaultCutoffDate } from "./Rechnungsvorschlag.js";
import type { VorschlagsClient } from "./Rechnungsvorschlag.js";
import { InvoiceFromOrderQuestion } from "./InvoiceFromOrderQuestion.js";
import { INVOICE_FROM_ORDER_WEB_TEXT, proposalCutoff } from "./invoiceFromOrderTexts.js";
import { Abrechnung } from "./Abrechnung.js";
import type { AbrechnungsClient } from "./Abrechnung.js";
import { AuftragsVolumen } from "./AuftragsVolumen.js";
import type { VolumenClient } from "./AuftragsVolumen.js";
import { Anhaenge, ANHAENGE_KOPFFELDER } from "./Anhaenge.js";
import type { AnhaengeClient } from "./Anhaenge.js";
import { AbrechnungsquellenKasten, hatQuellenStoerung, stoerungsText }
  from "./AbrechnungsquellenKasten.js";
import type { AbrechnungsquellenClient } from "./AbrechnungsquellenKasten.js";
import { formatBetrag, formatDatum } from "../format.js";
import { AUFTRAG_STATUS_ART, AUFTRAG_STATUS_LABEL } from "../vorgangsstatus.js";
import { CREATE_INVOICE_QUESTION, ORDER_STATUS_ACTION, STATUS_CHANGE_TEXT } from "./documentQuestionTexts.js";
import type { OrderStatusTarget } from "./documentQuestionTexts.js";
import { apiMeldung, betragText, betragZahl, entwuerfeGleich,
         kopfBetragText, LAUFZEIT_KOPFFELDER,
         nettobetragLabel, oderNull,
         PDF_HINWEIS_ART, speichereBlob, speichereParteien, speichereZahlplan,
         useParteienGriff, useZahlplanGriff } from "./vorgangsdetail.js";
import type { PdfMeldung } from "./vorgangsdetail.js";
// Die Klassennamen bleiben `kauf__` (Etappe 1, Aufgabe 2: keine Umbenennung an
// herausgelösten Bestandsteilen) — umgezogen ist in Fixrunde 1 nur die DATEI: Die Regeln
// dessen, was DIESER Baustein zeichnet, stehen jetzt hier statt im Stilblatt des Reiters.
import { hausfarbeChip } from "../markenfarben.js";
import { KiKnopf } from "./meHuellen.js";
import { HistoricalDetail } from "./historical/HistoricalDetail.js";
import type { HistoricalDetailClient } from "./historical/HistoricalDetail.js";
import { errorProp } from "./fieldErrors.js";
import "./AuftragDetail.css";

/**
 * Das AUFTRAGSDETAIL als eigener Baustein (Bereichs-Spalten, Etappe 1, Aufgabe 2) — die
 * Schwester von AngebotDetail und aus demselben Grund herausgelöst: Der Akten-Reiter und
 * der Auftrags-BEREICH (Aufgabe 4) sollen buchstäblich DASSELBE Detail zeichnen.
 * Der Umbau ist rein strukturell.
 *
 * Was der Baustein trägt: Kopfzeile (Nummer, Kürzel, Titel, Status), Datenzeile (Betrag,
 * Laufzeit-Ende, Herkunft aus einem Angebot, Ansprechpartner), das Bearbeiten samt
 * Positions-Editor, die Statusaktionen, „Als PDF" mitsamt der `fields[]`-Befundliste und die
 * Auftrag→Rechnung-Naht.
 *
 * Was er NICHT weiss: dass es eine Kundenakte gibt. Liste, Anlegen (auch das aus einem
 * Angebot vorbelegte), Meldungsstreifen und die Frage, WELCHER Vorgang bearbeitet wird,
 * gehören dem Rahmen und kommen als Eigenschaften und Rückrufe herein.
 */

/** Nur die Klientenmethoden, die das DETAIL braucht — Liste und Anlegen bleiben beim
 *  Rahmen (siehe `AuftraegeClient` in views/KundeAuftraege.tsx). */
export type AuftragDetailClient = Pick<ApiClient,
  "updateSalesOrder" | "setSalesOrderStatus" | "listContacts" | "listUsers"
  // „Neu laden“ im fremden Stand (ME-Etappe 3, Aufgabe 4) holt den Serverstand.
  | "getSalesOrder"
  | "listSelectablePaymentTerms" | "listPlaceholders" | "createInvoiceFromOrder"
  // Die Vorschau vor „Rechnung erzeugen“ (Spec 2026-11-02): offene Zahlplan-Posten in wählbaren Leistungsjahren.
  | "getInvoiceFromOrderPreview"
  | "renderOrder"
  // Belegvorschau (24.09.2026): die Seitenbilder der Auftragsbestätigung.
  | "vorschauOrder">
  & PositionsEditorClient
  // Lieferanten Strang E (Aufgabe 18): die Kontierung je Position.
  & PositionAssignment["client"]
  & BelegParteienModalClient
  & BelegLayoutClient
  & ZahlplanModalClient
  & FaelligkeitenClient
  & VorschlagsClient
  & AbrechnungsClient
  & VolumenClient
  // Der Kasten „Anlagen aus Textbausteinen" (465) (Paket „Textbausteine als Anhang", 24.09.2026) — am Auftrag NUR
  // lesend und mit dem Ausblenden-Schalter; kein Modal-Schlitz (siehe Kopfkommentar an der
  // Einhängung weiter unten).
  & AnhaengeClient
  // Der Kasten „Abrechnungsquellen" (Paket „Abrechnungsquellen", 29.09.2026).
  & AbrechnungsquellenClient
  // Der Abschnitt „Dateien“ (Aufgabe 25), mit den Dateien des Herkunftsangebots lesend (F6/F7).
  & DocumentFilesClient
  // Der historische Auftrag (Aufgabe 26): lesendes Detail, Bearbeiten im eigenen Panel samt Herkunftsangebot.
  & HistoricalDetailClient<"order">;

/**
 * Die AUSWAHLLISTEN, die Anlege- und Bearbeitungsmaske gemeinsam brauchen. Sie werden vom
 * RAHMEN geladen (`useAuftraegeUmfeld`) und hier hereingereicht, nicht je Detail geholt:
 * Sonst zöge jede Listenzeile ihre eigenen Ansprechpartner, Benutzer, Konditionen und
 * Platzhalter — vier Abrufe mal zwanzig Zeilen, für Daten, die für alle dieselben sind.
 */
export type AuftraegeUmfeldDaten = {
  kontakte: Ansprechpartner[];
  benutzer: Benutzer[];
  konditionen: KonditionAuswahl[];
  platzhalter: string[];
};

/**
 * Lädt die Auswahllisten einer Auftrags-Ansicht: aktive Ansprechpartner des Kunden, aktive
 * Benutzer, die wählbaren Zahlungskonditionen und die Platzhalternamen.
 *
 * Scheitert einer der Abrufe, bleibt die Ansicht bedienbar — die Felder sind ohnehin
 * optional, und die Vorschau lässt dann schlicht alle Tokens stehen.
 */
export function useAuftraegeUmfeld(
  client: Pick<AuftragDetailClient,
    "listContacts" | "listUsers" | "listSelectablePaymentTerms" | "listPlaceholders">,
  kundeId: string,
): AuftraegeUmfeldDaten {
  const [kontakte, setKontakte] = useState<Ansprechpartner[]>([]);
  const [benutzer, setBenutzer] = useState<Benutzer[]>([]);
  const [konditionen, setKonditionen] = useState<KonditionAuswahl[]>([]);
  const [platzhalter, setPlatzhalter] = useState<string[]>([]);

  useEffect(() => {
    let ab = false;
    client.listContacts(kundeId)
      .then(({ items }) => { if (!ab) setKontakte(items.filter((k) => k.status === "ACTIVE")); })
      .catch(() => { /* optionales Feld — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client, kundeId]);

  useEffect(() => {
    let ab = false;
    client.listUsers()
      .then(({ items }) => { if (!ab) setBenutzer(items.filter((b) => b.active)); })
      .catch(() => { /* optionales Feld — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client]);

  useEffect(() => {
    let ab = false;
    client.listSelectablePaymentTerms()
      .then(({ items }) => { if (!ab) setKonditionen(items); })
      .catch(() => { /* optionales Feld — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client]);

  useEffect(() => {
    let ab = false;
    client.listPlaceholders()
      .then(({ variablen }) => { if (!ab) setPlatzhalter(variablen); })
      .catch(() => { /* Auswahlhilfe — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client]);

  return { kontakte, benutzer, konditionen, platzhalter };
}

type OrderStatus = Auftrag["status"];

/**
 * Die erlaubten Statusübergänge — bewusst hier NACHGEBILDET, nicht aus @kundeninfo/domain
 * importiert: web darf nur `ui` und `api-client` importieren (check-boundaries). Die Regel ist
 * die aus der Spec (salesOrderUebergangErlaubt): aus ACTIVE nach COMPLETED oder CANCELLED; beide
 * sind Endzustände und bieten nichts mehr an. Der Server prüft denselben Übergang noch einmal
 * (422), das hier ist die Bedienführung, nicht die Wahrheit.
 */
function erlaubteFolgestatus(von: OrderStatus): OrderStatusTarget[] {
  if (von === "ACTIVE") return ["COMPLETED", "CANCELLED"];
  return [];
}


/** Die Formularwerte. Alle Angaben sind Zeichenketten (ausser dem openEnded-Schalter), damit
 *  ein geleertes Feld erst beim Absenden zu null wird. */
export type AuftragEntwurf = {
  companyId: string;
  title: string;
  netAmount: string;
  contactId: string;
  responsibleUserId: string;
  paymentTermId: string;
  customerPurchaseOrderNumber: string;
  orderedOn: string;
  startDate: string;
  endDate: string;
  openEnded: boolean;
  /** Rohtexte MIT ihren `#{…}`-Tokens — gespeichert wird genau das, aufgelöst wird erst
   *  fürs PDF (die Vorschau darunter ist nur eine Lesehilfe). Am Auftrag sind sie das
   *  VEREINBARTE, nicht mehr das Angebotene. */
  introText: string;
  outroText: string;
  /**
   * Die Laufzeitfelder aus Migration 031. Die WERTE als Zeichenkette, damit ein geleertes
   * Feld beim Absenden zu `null` wird statt zu 0 — „keine Mindestlaufzeit" und „null
   * Monate" sind zwei verschiedene Aussagen.
   *
   * Die EINHEITEN behalten ihren Wert auch bei leerem Feld: Sonst würde eine einmal
   * gewählte Angabe „Jahre" durch ein Leeren des Zahlenfeldes still zu „Monate"
   * (dieselbe Überlegung wie bei `noticeReference` seit Migration 008). Vorher gab es hier
   * nur zwei nackte Monatszahlen (`minimumTermMonths`/`renewalPeriodMonths`) und keine
   * Vertragslaufzeit — Migration 031 macht daraus vier Paare aus Zahl und Einheit.
   */
  termValue: string;
  termUnit: "MONTHS" | "YEARS";
  minimumTermValue: string;
  minimumTermUnit: "MONTHS" | "YEARS";
  renewalPeriodValue: string;
  renewalPeriodUnit: "MONTHS" | "YEARS";
  noticePeriodValue: string;
  noticePeriodUnit: "MONTHS" | "YEARS";
  noticeReference: "TERM_END" | "CALENDAR_YEAR_END";
  /** Die Periodenregel des Zahlplans (Migration 026). Beim Ableiten aus einem Angebot
   *  (Ruling Z10) kommt sie von DORT und ist im Anlegeformular gesperrt — siehe
   *  `auftragAnlegen.ts`; am gespeicherten Auftrag ist sie danach gewöhnlich änderbar. */
  periodBasis: "CALENDAR_YEAR" | "CONTRACT_YEAR";
};

export function leererAuftragEntwurf(companies: Unternehmen[]): AuftragEntwurf {
  return {
    // Bei genau einem Unternehmen (der häufige Fall: eine Kundenbeziehung) ist es vorgewählt;
    // bei mehreren muss bewusst gewählt werden.
    companyId: companies.length === 1 ? companies[0]!.id : "",
    title: "", netAmount: "", contactId: "", responsibleUserId: "", paymentTermId: "",
    customerPurchaseOrderNumber: "", orderedOn: heuteAlsTag(), startDate: "", endDate: "",
    openEnded: false, introText: "", outroText: "",
    termValue: "", termUnit: "MONTHS",
    minimumTermValue: "", minimumTermUnit: "MONTHS",
    renewalPeriodValue: "", renewalPeriodUnit: "MONTHS",
    noticePeriodValue: "", noticePeriodUnit: "MONTHS", noticeReference: "TERM_END",
    periodBasis: "CALENDAR_YEAR",
  };
}

export function auftragEntwurfAus(a: Auftrag): AuftragEntwurf {
  return {
    companyId: a.companyId,
    title: a.title,
    netAmount: betragText(a.netAmount),
    contactId: a.contactId ?? "",
    responsibleUserId: a.responsibleUserId ?? "",
    paymentTermId: a.paymentTermId ?? "",
    customerPurchaseOrderNumber: a.customerPurchaseOrderNumber ?? "",
    orderedOn: a.orderedOn,
    startDate: a.startDate ?? "",
    endDate: a.endDate ?? "",
    openEnded: a.openEnded,
    introText: a.introText ?? "",
    outroText: a.outroText ?? "",
    termValue: a.termValue === null ? "" : String(a.termValue),
    termUnit: a.termUnit ?? "MONTHS",
    minimumTermValue: a.minimumTermValue === null ? "" : String(a.minimumTermValue),
    minimumTermUnit: a.minimumTermUnit ?? "MONTHS",
    renewalPeriodValue: a.renewalPeriodValue === null ? "" : String(a.renewalPeriodValue),
    renewalPeriodUnit: a.renewalPeriodUnit ?? "MONTHS",
    noticePeriodValue: a.noticePeriodValue === null ? "" : String(a.noticePeriodValue),
    // Beide Felder tragen im Vertrag ihre Aufzählung, der erzeugte Client also die enge
    // Vereinigung — hier bleibt nur der Vorgabewert für „noch keine Frist erfasst".
    noticePeriodUnit: a.noticePeriodUnit ?? "MONTHS",
    noticeReference: a.noticeReference ?? "TERM_END",
    periodBasis: a.periodBasis,
  };
}

/**
 * Die ABSCHNITTE der Auftragsmaske in der Folge der Spec-Tabelle „Anwendung auf die drei Vorgangsdialoge“ (W14). Eine
 * Notiz führt der Auftrag nicht (Ruling 3 zu Aufgabe 9: die Zeile „Notiz“ gilt nur für das Angebot); dafür stehen
 * Abrechnungsquellen und „Abrechnung und Volumen“ zwischen Zahlung und Texten.
 */
export type AuftragAbschnitt = "head" | "parties" | "term" | "payment" | "billingSources" | "billingVolume"
  | "intro" | "items" | "outro" | "textAttachments" | "files";

/**
 * Die Felder je Abschnitt: SERVER-Feldname → Endung der Feld-Id im Formular (Bauart `KopffeldTabelle`,
 * vorgangsdetail.ts) — Begründung an `ANGEBOT_ABSCHNITTSFELDER`.
 *
 * `endDate` steht in den Kopfdaten, obwohl das Feld bei „Unbefristet" gar nicht gezeichnet wird: Genau dann meldet der
 * Server „Ein unbefristeter Auftrag hat kein Enddatum", und dann soll der Abschnitt sich öffnen — sichtbar wird dabei
 * der Schalter, um den es in Wahrheit geht.
 *
 * In den PARTEIEN steht `purchaseOrderNumber` mit, obwohl der Auftrag das Feld dort nicht zeigt (er führt die Nummer
 * als `customerPurchaseOrderNumber` in den Kopfdaten): Zeigt der Server einmal doch darauf, soll wenigstens der
 * Abschnitt aufklappen; `zeigeFeld` verträgt die Kennung ins Leere.
 */
export const AUFTRAG_ABSCHNITTSFELDER: SectionFields<AuftragAbschnitt> = {
  head: { companyId: "company", title: "title", netAmount: "amount", orderedOn: "ordered",
          customerPurchaseOrderNumber: "po", paymentTermId: "payterm", contactId: "contact",
          responsibleUserId: "user", startDate: "start", endDate: "end" },
  parties: PARTEIEN_KOPFFELDER,
  term: { ...LAUFZEIT_KOPFFELDER, periodBasis: "periodbasis" },
  payment: ZAHLPLAN_KOPFFELDER,
  billingSources: {},
  billingVolume: {},
  intro: { introText: "intro" },
  items: {},
  outro: { outroText: "outro" },
  textAttachments: ANHAENGE_KOPFFELDER,
  files: {},
};

/**
 * Der Startzustand beim BEARBEITEN (Spec-Tabelle, Spalte „Start Bearbeiten“): alles zu außer den Positionen — dort
 * findet die Arbeit statt; die Texte offen, wenn Text da ist.
 */
export function AUFTRAG_START_BEARBEITEN(entwurf: AuftragEntwurf): Record<AuftragAbschnitt, boolean> {
  return {
    head: false, parties: false, term: false, payment: false, billingSources: false, billingVolume: false,
    intro: hasText(entwurf.introText), items: true, outro: hasText(entwurf.outroText), textAttachments: false,
    files: false,
  };
}

/** Beim ANLEGEN ist alles offen: Am leeren Vorgang gibt es nichts zu übergehen. */
export const ORDER_START_CREATE: Readonly<Record<AuftragAbschnitt, boolean>> = {
  head: true, parties: true, term: true, payment: true, billingSources: true, billingVolume: true,
  intro: true, items: true, outro: true, textAttachments: true, files: true,
};

/** Die Abschnitte des geschlossenen Details; „Dateien“ ist dort bedienbar (Spec „Abschnitt ‚Dateien‘“). */
export type OrderReadSection = "payment" | "billingSources" | "billingVolume" | "textAttachments" | "files";

/**
 * Geschlossenes Detail: alles zu — außer den Abrechnungsquellen eines gestörten Auftrags (Spec „Abrechnungsquellen“:
 * „Der Kasten selbst klappt in diesem Fall vorbelegt auf“). Ein geschlossener Abschnitt fragt nicht nach (kein Abruf je
 * Listenzeile, W35).
 */
export function orderStartReadOnly(disturbed: boolean): Record<OrderReadSection, boolean> {
  return { payment: false, billingSources: disturbed, billingVolume: false, textAttachments: false, files: false };
}

/**
 * Der Befund der Feldprüfung: der Satz für den Bearbeiter UND das Feld, um das es geht —
 * bei seinem SERVERNAMEN, damit Masken- und Serverweg durch dieselbe Tabelle laufen
 * (siehe `AngebotBefund`, dieselbe Überlegung).
 *
 * Alle vier Pflichtangaben stehen in den eingefalteten Kopfdaten; eingeklappt sähe niemand
 * einen Satz an einem Feld, das gar nicht zu sehen ist.
 */
export type AuftragBefund = {
  meldung: string;
  feld: "companyId" | "title" | "netAmount" | "orderedOn";
};

/** Die gemeinsame Prüfung für Anlegen und Bearbeiten (null = in Ordnung). */
export function pruefeAuftrag(
  entwurf: AuftragEntwurf, brauchtCompany: boolean,
): AuftragBefund | null {
  if (brauchtCompany && entwurf.companyId === "") {
    return { meldung: "Bitte ein Unternehmen wählen.", feld: "companyId" };
  }
  if (entwurf.title.trim() === "") {
    return { meldung: "Bitte einen Titel angeben.", feld: "title" };
  }
  if (Number.isNaN(betragZahl(entwurf.netAmount))) {
    return { meldung: "Bitte einen gültigen Betrag angeben.", feld: "netAmount" };
  }
  if (entwurf.orderedOn.trim() === "") {
    return { meldung: "Bitte das Bestelldatum angeben.", feld: "orderedOn" };
  }
  return null;
}

/** Den Abschnitt auf das Feld öffnen, das ein Befund der MASKE nennt, und es fokussieren. */
export function zeigeAuftragsFeld(
  f: SectionFolds<AuftragAbschnitt>, praefix: string, feld: string,
): void {
  showFieldIn(f, AUFTRAG_ABSCHNITTSFELDER, praefix, feld);
}

/** Dasselbe für den ersten Befund des SERVERS (422 mit `ApiError.fields[]`); ohne Befunde bleibt alles zu. */
export function zeigeAuftragsFehlerFeld(
  f: SectionFolds<AuftragAbschnitt>, praefix: string, fehler: unknown,
): void {
  showFindingIn(f, AUFTRAG_ABSCHNITTSFELDER, praefix, fehler);
}

export type AuftragKopffelder = {
  title: string; netAmount: number; contactId: string | null;
  responsibleUserId: string | null; paymentTermId: string | null;
  customerPurchaseOrderNumber: string | null; orderedOn: string;
  startDate: string | null; endDate: string | null; openEnded: boolean;
  introText: string | null; outroText: string | null;
  termValue: number | null; termUnit: "MONTHS" | "YEARS" | null;
  minimumTermValue: number | null; minimumTermUnit: "MONTHS" | "YEARS" | null;
  renewalPeriodValue: number | null; renewalPeriodUnit: "MONTHS" | "YEARS" | null;
  noticePeriodValue: number | null;
  noticePeriodUnit: "MONTHS" | "YEARS" | null;
  noticeReference: "TERM_END" | "CALENDAR_YEAR_END" | null;
  periodBasis: "CALENDAR_YEAR" | "CONTRACT_YEAR";
};

/** Die Kopf- und Laufzeitfelder, die Anlegen und Ändern teilen (companyId/offerId gehören nur
 *  zum Anlegen — der Server lässt die Herkunft beim Ändern nicht zu). openEnded und endDate
 *  sind konsistent: ist unbefristet gesetzt, geht das Endedatum als null mit. */
export function auftragKopffelder(entwurf: AuftragEntwurf): AuftragKopffelder {
  /** Ein Wert/Einheit-PAAR für den Server: leer heisst BEIDES null. Ausgeschrieben und
   *  nicht gestreut, weil `exactOptionalPropertyTypes` ein `undefined` anders behandelt
   *  als einen fehlenden Schlüssel. */
  function paar(wert: string, einheit: "MONTHS" | "YEARS") {
    const kern = wert.trim();
    return kern === ""
      ? { wert: null, einheit: null }
      : { wert: Number(kern), einheit };
  }
  const term = paar(entwurf.termValue, entwurf.termUnit);
  const mindest = paar(entwurf.minimumTermValue, entwurf.minimumTermUnit);
  const verlaengerung = paar(entwurf.renewalPeriodValue, entwurf.renewalPeriodUnit);
  const frist = paar(entwurf.noticePeriodValue, entwurf.noticePeriodUnit);
  return {
    title: entwurf.title.trim(),
    // Mit Positionen ist der Betrag berechnet; das Feld zeigt ihn nur an und schickt ihn
    // unverändert zurück (der Server nimmt genau diesen Wert an, siehe gepruefterBetrag).
    netAmount: betragZahl(entwurf.netAmount),
    contactId: oderNull(entwurf.contactId),
    responsibleUserId: oderNull(entwurf.responsibleUserId),
    paymentTermId: oderNull(entwurf.paymentTermId),
    customerPurchaseOrderNumber: oderNull(entwurf.customerPurchaseOrderNumber),
    orderedOn: entwurf.orderedOn,
    startDate: oderNull(entwurf.startDate),
    endDate: entwurf.openEnded ? null : oderNull(entwurf.endDate),
    openEnded: entwurf.openEnded,
    introText: oderNull(entwurf.introText),
    outroText: oderNull(entwurf.outroText),
    termValue: term.wert, termUnit: term.einheit,
    minimumTermValue: mindest.wert, minimumTermUnit: mindest.einheit,
    renewalPeriodValue: verlaengerung.wert, renewalPeriodUnit: verlaengerung.einheit,
    noticePeriodValue: frist.wert, noticePeriodUnit: frist.einheit,
    // Der BEZUGSPUNKT hängt weiter an der Frist: Ohne Zahl gibt es keinen Stichtag.
    noticeReference: frist.wert === null ? null : entwurf.noticeReference,
    periodBasis: entwurf.periodBasis,
  };
}

/** Die Werte für die Platzhalter-Vorschau — genau das, was die Ansicht ohnehin geladen hat.
 *  Was fehlt, erscheint gar nicht erst und bleibt als sichtbares Token stehen. */
export function auftragVorschauWerte(arg: {
  entwurf: AuftragEntwurf;
  companies: Unternehmen[];
  kontakte: Ansprechpartner[];
  kundeName?: string | undefined;
}): Record<string, string> {
  const { entwurf, companies, kontakte, kundeName } = arg;
  const werte: Record<string, string> = {
    ...laufzeitVorschauWerte({
      termValue: entwurf.termValue, termUnit: entwurf.termUnit,
      minimumTermValue: entwurf.minimumTermValue, minimumTermUnit: entwurf.minimumTermUnit,
      renewalPeriodValue: entwurf.renewalPeriodValue,
      renewalPeriodUnit: entwurf.renewalPeriodUnit,
      noticePeriodValue: entwurf.noticePeriodValue,
      noticePeriodUnit: entwurf.noticePeriodUnit,
      noticeReference: entwurf.noticeReference,
    }),
    "datum.heute": formatDatum(new Date().toISOString()),
  };
  if (kundeName !== undefined && kundeName !== "") werte["kunde.name"] = kundeName;
  const firma = companies.find((c) => c.id === entwurf.companyId);
  if (firma !== undefined) werte["firma.name"] = firma.name;
  const kontakt = kontakte.find((k) => k.id === entwurf.contactId);
  if (kontakt !== undefined) {
    werte["ansprechpartner.name"] = kontakt.anzeigename;
    // Migration 025: die Briefanrede steht NEBEN dem Namen (masterdataRules.ts) — sie ist
    // eine Ableitung aus Geschlecht, Titel und Namen, kein eigenes Eingabefeld.
    werte["ansprechpartner.anrede"] = kontakt.briefanrede;
  }
  return werte;
}

export type AuftragFelderArgs = {
  praefix: string;
  mitUnternehmen: boolean;
  companies: Unternehmen[];
  umfeld: AuftraegeUmfeldDaten;
  entwurf: AuftragEntwurf;
  setEntwurf: Dispatch<SetStateAction<AuftragEntwurf>>;
  /** Hat der Vorgang Positionen? Dann ist der Nettobetrag berechnet und nur noch Anzeige. */
  hatPositionen: boolean;
  /** Die Währung des Vorgangs für die Zusammenfassung der Kopfdaten; beim Anlegen die Vorgabe (EUR). */
  currency?: string | undefined;
  /** Die Sammelmeldung am Fuss der Maske (Prüfung der Maske, Fehler ohne Feldbezug). */
  feldFehler: string | null;
  /** Die Befunde JE FELD (ME-Etappe 3, Aufgabe 4) — siehe `AngebotFelderArgs.fehlerJeFeld`. */
  fehlerJeFeld?: Readonly<Record<string, string>> | undefined;
  /** Die Formularzustände für das Raster der Maske — siehe `AngebotFelderArgs.formular`. */
  formular?: {
    ref?: RefObject<FormularElement | null>;
    laeuft: boolean;
    fremderStand?: boolean;
    onNeuLaden?: () => void;
    hinweis?: ReactNode;
  } | undefined;
  vorschauWerte: Record<string, string>;
  /** Die Faltungen je Abschnitt — sie gehören der MASKE (siehe components/Kopffaltung.tsx), weil nur sie den
   *  Startzustand und die Rücksetzpunkte kennt. */
  faltungen: SectionFolds<AuftragAbschnitt>;
  /**
   * Die Abschnitte, deren Inhalt am GELADENEN Vorgang samt Version hängt — als Schlitz (Bauart und Begründung an
   * `AngebotFelderArgs`): Beim ANLEGEN gibt es den Vorgang noch nicht, dort fehlen sie samt Abschnitt.
   *
   *  - `parteien`: Käufer und Anschriften (zweiter Speicherweg `setBelegParteien`).
   *  - `zahlplan`: die Zahlungsvereinbarung (dritter Speicherweg).
   *  - `billingSources`, `billingVolume`: die Kästen „Abrechnungsquellen“ und „Abrechnung und Volumen“ — sie
   *    schreiben bzw. lesen selbst und laden erst beim Aufklappen.
   *  - `positionen`: der Positions-Editor zwischen Einleitungs- und Ausleitungstext.
   *  - `anhaenge`: die Anlagen aus Textbausteinen, am Auftrag lesend und ausblendbar.
   *  - `dateien`: der Abschnitt „Dateien“ (`filesContent`).
   */
  parteien?: SectionContent | undefined;
  zahlplan?: SectionContent | undefined;
  billingSources?: SectionContent | undefined;
  billingVolume?: SectionContent | undefined;
  positionen?: SectionContent | undefined;
  anhaenge?: SectionContent | undefined;
  dateien?: SectionContent | undefined;
  /**
   * Die Periodenregel ist GESPERRT (Festlegung Z10): Dieser Auftrag wird aus einem
   * Angebot abgeleitet, und Periodenregel und Raten sind EINE Vereinbarung — sie kommt
   * aus dem Angebot, nicht aus einer zweiten, im Anlegeformular getroffenen Wahl. Nur die
   * ANLAGE kennt diesen Fall (`useAuftragAnlegen`); am gespeicherten Auftrag ist das Feld
   * gewöhnlich änderbar, deshalb ist die Vorgabe `false`.
   */
  periodBasisGesperrt?: boolean;
};

/**
 * Die Felder von Anlege- und Bearbeitungsmaske. Bewusst eine Funktion, die JSX ZURÜCKGIBT, und
 * keine eigene Komponente: eine im Render definierte Komponente bekäme bei jedem Neuzeichnen
 * eine neue Identität — React hängte ihren Teilbaum ab und neu auf, und der Fokus spränge nach
 * jedem getippten Zeichen aus dem Feld (Lehre aus KundeAnsprechpartner/KundeAngebote). Sie ist
 * auch nach dem Herauslösen des Bausteins gemeinsam: Der Rahmen zeichnet mit ihr seine
 * ANLAGE, das Detail sein Bearbeiten.
 *
 * **Die Folge ist die der Spec-Tabelle „Anwendung auf die drei Vorgangsdialoge“** (W14): Kopfdaten, Parteien, Laufzeit
 * und Kündigung, Zahlungsvereinbarung und Fälligkeiten, Abrechnungsquellen, Abrechnung und Volumen, Einleitungstext,
 * POSITIONEN, Ausleitungstext, Anlagen aus Textbausteinen — jeder ein `Vorgangsabschnitt` mit seiner Objektart. Eine
 * Notiz führt der Auftrag nicht (Ruling 3 zu Aufgabe 9).
 */
export function auftragFormularFelder(a: AuftragFelderArgs): ReactElement {
  const { praefix, umfeld, entwurf, setEntwurf, faltungen } = a;
  const f = a.formular;
  const { section, slotSection, textSection } = sectionRenderer(faltungen, praefix,
    findingsPerSection(AUFTRAG_ABSCHNITTSFELDER, a.fehlerJeFeld));
  /** Einleitungs- und Ausleitungstext: Rohtext mit Tokens, darunter die leichte Vorschau (aus der wiederverwendeten
   *  Editor-Komponente). Markdown, wächst mit; zugeklappt zeigt der Kopf die erste Zeile. */
  function text(k: "intro" | "outro", field: "introText" | "outroText"): ReactElement {
    return textSection(k, SECTION_TEXT[k], {
      id: `${praefix}-${k}`, wert: entwurf[field], onChange: draftSetter(setEntwurf)(field),
      variablen: umfeld.platzhalter, werte: a.vorschauWerte,
    });
  }
  return (
    /* Das Raster der Maske (`me-formular`, ME-Etappe 3, Aufgabe 4) — wie am Angebot um die
       GANZE Maske (Hinweiszeile „fremder Stand“ auch bei zugeklappten Abschnitten sichtbar),
       Beschriftung oben. */
    <Formular beschriftung="oben"
      {...(f?.ref !== undefined ? { ref: f.ref } : {})}
      laeuft={f?.laeuft === true} fremderStand={f?.fremderStand === true}
      {...(f?.onNeuLaden !== undefined ? { onNeuLaden: f.onNeuLaden } : {})}
      {...(f?.hinweis !== undefined ? { hinweis: f.hinweis } : {})}>
      <div className="kauf__maske">
        {section("head", "auftrag", SECTION_TEXT.headTitle(entwurf.title), {
          inhalt: auftragKopffeldRaster(a),
          zusammenfassung: SECTION_TEXT.headSummary(kopfBetragText(entwurf.netAmount, a.currency),
            a.hatPositionen ? SECTION_TEXT.netComputed : SECTION_TEXT.net),
        })}
        {slotSection("parties", "kunde", SECTION_TEXT.parties, a.parteien)}
        {section("term", "vertrag", SECTION_TEXT.term, { inhalt: orderTermGrid(a), zusammenfassung: termSummary(entwurf) })}
        {slotSection("payment", "abrechnung", SECTION_TEXT.paymentOrder, a.zahlplan)}
        {slotSection("billingSources", "messstelle", SECTION_TEXT.billingSources, a.billingSources)}
        {slotSection("billingVolume", "abrechnung", SECTION_TEXT.billingVolume, a.billingVolume)}
        {text("intro", "introText")}
        {slotSection("items", "position", SECTION_TEXT.items, a.positionen)}
        {text("outro", "outroText")}
        {slotSection("textAttachments", "textbaustein", SECTION_TEXT.textAttachments, a.anhaenge)}
        {slotSection("files", "datei", SECTION_TEXT.files, a.dateien)}

        {/* Die Sammelmeldung steht am Ende der Maske, dicht über der Fußleiste: Dort wurde
            „Speichern" gedrückt. Befunde an einzelnen Feldern stehen AM FELD; betrifft
            einer ein Feld in einem zugeklappten Abschnitt, hat die Maske ihn aufgeklappt. */}
        {a.feldFehler !== null && <Hinweis art="fehler">{a.feldFehler}</Hinweis>}
      </div>
    </Formular>
  );
}

/** Das zweispaltige Raster der KOPFDATEN — der Inhalt ihres Abschnitts (ohne Laufzeit und Periodenregel). Eigene
 *  Funktion allein der Lesbarkeit halber: Sie hat keinen eigenen Zustand und keine eigene
 *  Identität (kein Komponententyp), zeichnet also weiterhin im selben Zug wie die Maske.
 *
 *  Seit ME-Etappe 3 (Aufgabe 4) auf den Hüllen: `Feld`, `Auswahl` und — für „Unbefristet“,
 *  ein echtes Ja/Nein — `Schalter` (me-schalter). Die Kennungen bleiben. */
function auftragKopffeldRaster(a: AuftragFelderArgs): ReactElement {
  const { praefix, companies, umfeld, entwurf, setEntwurf, hatPositionen } = a;
  const fj = a.fehlerJeFeld;
  const setze = draftSetter(setEntwurf);
  return (
    <div className="kauf__felder">
      {a.mitUnternehmen && (
        <Auswahl id={`${praefix}-company`} name={`${praefix}-company`}
          beschriftung="Unternehmen" wert={entwurf.companyId}
          {...errorProp(fj?.companyId)}
          onGeaendert={(d) => setze("companyId")(d.wert)}>
          {companies.length !== 1 && <option value="">Bitte wählen …</option>}
          {companies.map((c) => (
            <option key={c.id} value={c.id}>{c.shortCode} · {c.name}</option>
          ))}
        </Auswahl>
      )}

      <Feld id={`${praefix}-title`} label="Titel" bearbeitbar wert={entwurf.title}
        onChange={setze("title")} {...errorProp(fj?.title)} />

      {/* Sobald der Vorgang Positionen hat, ist der Betrag BERECHNET: Das Feld zeigt ihn
          nur noch an (BETRAG_BERECHNET). `gesperrt` statt `disabled`: Der Wert bleibt
          lesbar, kopierbar und geht beim Speichern unverändert mit. Ein Betrag ist TEXT
          (deutsches Komma), kein Zahlenfeld. */}
      <Feld id={`${praefix}-amount`} label={nettobetragLabel(hatPositionen)} bearbeitbar
        wert={entwurf.netAmount} onChange={setze("netAmount")} gesperrt={hatPositionen}
        {...errorProp(fj?.netAmount)} />

      <Feld id={`${praefix}-ordered`} label="Bestellt am" typ="date" bearbeitbar
        wert={entwurf.orderedOn} onChange={setze("orderedOn")}
        {...errorProp(fj?.orderedOn)} />

      <Feld id={`${praefix}-po`} label="Kunden-Bestellnummer" bearbeitbar
        wert={entwurf.customerPurchaseOrderNumber}
        onChange={setze("customerPurchaseOrderNumber")}
        {...errorProp(fj?.customerPurchaseOrderNumber)} />

      <Auswahl id={`${praefix}-payterm`} name={`${praefix}-payterm`}
        beschriftung="Zahlungskondition" wert={entwurf.paymentTermId}
        {...errorProp(fj?.paymentTermId)}
        onGeaendert={(d) => setze("paymentTermId")(d.wert)}>
        <option value="">— keine —</option>
        {umfeld.konditionen.map((k) => (
          <option key={k.id} value={k.id}>{k.name}</option>
        ))}
      </Auswahl>

      <Auswahl id={`${praefix}-contact`} name={`${praefix}-contact`}
        beschriftung="Ansprechpartner" wert={entwurf.contactId}
        {...errorProp(fj?.contactId)} onGeaendert={(d) => setze("contactId")(d.wert)}>
        <option value="">— keiner —</option>
        {umfeld.kontakte.map((k) => (
          <option key={k.id} value={k.id}>{k.anzeigename}</option>
        ))}
      </Auswahl>

      <Auswahl id={`${praefix}-user`} name={`${praefix}-user`}
        beschriftung="Verantwortlich" wert={entwurf.responsibleUserId}
        {...errorProp(fj?.responsibleUserId)}
        onGeaendert={(d) => setze("responsibleUserId")(d.wert)}>
        <option value="">— niemand —</option>
        {umfeld.benutzer.map((b) => (
          <option key={b.id} value={b.id}>{b.name}</option>
        ))}
      </Auswahl>

      <Feld id={`${praefix}-start`} label="Laufzeitbeginn" typ="date" bearbeitbar
        wert={entwurf.startDate} onChange={setze("startDate")}
        {...errorProp(fj?.startDate)} />

      {/* Der Unbefristet-Schalter blendet das Endedatum aus — dieselbe Konsistenz, die der
          Server erzwingt (openEnded ⇒ endDate = null). Ein Befund am (dann verborgenen)
          Enddatum steht am Schalter: Um ihn geht es in Wahrheit. */}
      <div className="kauf__feld kauf__feld--schalter">
        <Schalter id={`${praefix}-open`} name={`${praefix}-open`} beschriftung="Unbefristet"
          an={entwurf.openEnded}
          {...(entwurf.openEnded ? errorProp(fj?.endDate) : {})}
          onGeaendert={(d) => setze("openEnded")(d.an)} />
      </div>

      {!entwurf.openEnded && (
        <Feld id={`${praefix}-end`} label="Laufzeitende" typ="date" bearbeitbar
          wert={entwurf.endDate} onChange={setze("endDate")}
          {...errorProp(fj?.endDate)} />
      )}

    </div>
  );
}

/** Das Raster des Abschnitts LAUFZEIT UND KÜNDIGUNG: die vier Paare, der Bezugspunkt der Frist und die Periodenregel
 *  (sie gilt für den ganzen Beleg, deshalb bei der Laufzeit und nicht je Rate). */
function orderTermGrid(a: AuftragFelderArgs): ReactElement {
  const { praefix, entwurf, setEntwurf } = a;
  const fj = a.fehlerJeFeld;
  const setze = draftSetter(setEntwurf);
  return (
    <div className="kauf__felder">
      {/* DIE LAUFZEIT (Migration 031, Entscheid E1): vier Dauern, jede als Zahl PLUS
          Einheit (`laufzeitPaar`, geteilt mit dem Angebot). Am Auftrag ist die Laufzeit
          das VEREINBARTE. */}
      {laufzeitPaar({ block: "kauf", id: `${praefix}-term`, label: "Vertragslaufzeit",
        einheitLabel: "Einheit der Vertragslaufzeit", wert: entwurf.termValue,
        einheit: entwurf.termUnit, onWert: setze("termValue"), onEinheit: setze("termUnit"),
        fehler: fj?.["termValue"], einheitFehler: fj?.["termUnit"] })}
      {laufzeitPaar({ block: "kauf", id: `${praefix}-minterm`, label: "Mindestlaufzeit",
        einheitLabel: "Einheit der Mindestlaufzeit", wert: entwurf.minimumTermValue,
        einheit: entwurf.minimumTermUnit, onWert: setze("minimumTermValue"),
        onEinheit: setze("minimumTermUnit"),
        fehler: fj?.["minimumTermValue"], einheitFehler: fj?.["minimumTermUnit"] })}
      {laufzeitPaar({ block: "kauf", id: `${praefix}-renewal`, label: "Verlängerung",
        einheitLabel: "Einheit der Verlängerung", wert: entwurf.renewalPeriodValue,
        einheit: entwurf.renewalPeriodUnit, onWert: setze("renewalPeriodValue"),
        onEinheit: setze("renewalPeriodUnit"),
        fehler: fj?.["renewalPeriodValue"], einheitFehler: fj?.["renewalPeriodUnit"] })}
      {laufzeitPaar({ block: "kauf", id: `${praefix}-notice`, label: "Kündigungsfrist",
        einheitLabel: "Einheit der Kündigungsfrist", wert: entwurf.noticePeriodValue,
        einheit: entwurf.noticePeriodUnit, onWert: setze("noticePeriodValue"),
        onEinheit: setze("noticePeriodUnit"),
        fehler: fj?.["noticePeriodValue"], einheitFehler: fj?.["noticePeriodUnit"] })}

      {/* Der Bezugspunkt „Kündigung wirkt" bleibt UNVERÄNDERT an seiner Stelle hinter der
          Frist — er ist kein eigenes Wert/Einheit-Paar, sondern die Wahl, worauf sich die
          Frist bezieht. */}
      <div className="kauf__feld kauf__feld--breit">
        <Auswahl id={`${praefix}-notice-ref`} name={`${praefix}-notice-ref`}
          beschriftung="Kündigung wirkt" wert={entwurf.noticeReference}
          {...errorProp(fj?.noticeReference)}
          onGeaendert={(d) => setze("noticeReference")(
            d.wert === "CALENDAR_YEAR_END" ? "CALENDAR_YEAR_END" : "TERM_END")}>
          <option value="TERM_END">zum Ende der Laufzeit</option>
          <option value="CALENDAR_YEAR_END">zum Ende des Kalenderjahres</option>
        </Auswahl>
      </div>

      {/* Die Periodenregel des Zahlplans (Migration 026) — beim Ableiten aus einem
          Angebot GESPERRT (Festlegung Z10): Periodenregel und Raten sind EINE
          Vereinbarung, und sie kommt dann aus dem Angebot. */}
      <div className="kauf__feld kauf__feld--breit">
        <Auswahl id={`${praefix}-periodbasis`} name={`${praefix}-periodbasis`}
          beschriftung="Leistungszeitraum" wert={entwurf.periodBasis}
          gesperrt={a.periodBasisGesperrt === true}
          {...errorProp(fj?.periodBasis)}
          onGeaendert={(d) => setze("periodBasis")(
            d.wert === "CONTRACT_YEAR" ? "CONTRACT_YEAR" : "CALENDAR_YEAR")}>
          <option value="CALENDAR_YEAR">Kalenderjahr</option>
          <option value="CONTRACT_YEAR">Vertragsjahr</option>
        </Auswahl>
        {a.periodBasisGesperrt === true
          ? <p className="kauf__hinweis">Kommt aus dem Angebot.</p>
          : (
            <p className="kauf__hinweis">
              Bei Kalenderjahr zählen erstes und letztes Jahr VOLL — Beträge und
              Rechnungen werden für das ganze Kalenderjahr aufgenommen.
            </p>
          )}
      </div>
    </div>
  );
}

export type AuftragDetailProps = {
  client: AuftragDetailClient;
  kundeId: string;
  /** Der Vorgang — fertig geladen, samt seiner `version`. Wie am Angebot bewusst der SATZ
   *  und nicht bloss die Kennung: Der Rahmen führt seine Liste ohnehin, ein Nachladen je
   *  Zeile wäre eine Verhaltensänderung. */
  auftrag: Auftrag;
  /** Die Unternehmen mit Kundenbeziehung — für das Kürzel und die Platzhalter-Vorschau. */
  companies: Unternehmen[];
  /** Die vom Rahmen geladenen Auswahllisten (`useAuftraegeUmfeld`). */
  umfeld: AuftraegeUmfeldDaten;
  /** Steht GENAU DIESER Vorgang gerade in Bearbeitung? */
  bearbeitung: boolean;
  /** Ist die Bedienung gesperrt, weil anderswo ein Formular offen ist? */
  gesperrt: boolean;
  /** Der Firmenname des Kunden — allein für die Platzhalter-Vorschau (`#{kunde.name}`). */
  kundeName?: string | undefined;
  /** Ist die Sitzung ADMIN? Allein für das Lösen von Abrechnungsquellen an nicht aktiven
   *  Aufträgen (Entscheid Dirk 29.09.2026, Punkt 7). Weglassbar — dann nicht; den echten
   *  Riegel trägt die Route. */
  istAdmin?: boolean | undefined;
  onBearbeiten: () => void;
  onSchliessen: () => void;
  /** Der Rahmen soll seine Liste neu holen (nach Statuswechsel, Speichern, 409). */
  onNeuLaden: () => void | Promise<unknown>;
  /** Der Meldungsstreifen gehört dem Rahmen — er steht über der ganzen Ansicht. */
  onHinweis: (text: string | null) => void;
  /** Der Vorgang ist weitergedreht (Version/Betrag aus dem Positions-Editor): Der Rahmen
   *  schreibt ihn in seine Liste fort und reicht ihn als `auftrag` zurück herein. Ohne das
   *  rechneten die Statusknöpfe mit einer veralteten Version. */
  onFortschreiben: (auftrag: Auftrag) => void;
  /**
   * Die Auftrag→Rechnung-Naht (Paket 3b-2). Ist sie gesetzt, erscheint „Rechnung erzeugen":
   * Der Baustein LEITET die Rechnung hier ab (`createInvoiceFromOrder`) und meldet den
   * fertigen Entwurf nach oben; der Rahmen wechselt daraufhin in den Rechnungen-Reiter.
   *
   * Warum die Ableitung HIER geschieht und nicht im Ziel (anders als bei Angebot→Auftrag):
   * Dort muss der Bearbeiter noch Felder ausfüllen, das Ziel bekommt also eine Vorbelegung
   * für seine Maske. Hier ist es ein einziger Serveraufruf ohne Eingabe — und wenn er
   * scheitert, soll die Meldung AM AUFTRAG stehen, nicht in einem frisch aufgeschlagenen,
   * leeren Rechnungen-Reiter.
   *
   * PFLICHT-Eigenschaft, obwohl sie `undefined` sein darf (Muster `onFortschreiben`): Wer
   * die Naht bewusst nicht verdrahtet, schreibt `undefined` hin und sieht dabei, dass er
   * einen Knopf abwählt. Wäre sie weglassbar, fehlte der Knopf nach einem Vergessen in
   * Aufgabe 4 still — und still fehlende Knöpfe fallen erst dem Bearbeiter auf.
   */
  onRechnungAusAuftrag: ((rechnung: Rechnung) => void) | undefined;
  /** Der Sprung „Ordner“ aus dem Abschnitt „Dateien“ in den Kundenreiter „Dateien“ (Akte bzw. Bereich). */
  onOrdnerOeffnen: (folderId: string) => void;
  /** Der Vorgang steht allein (Bereich) statt als Zeile der Akten-Liste. Wirkt nur am historischen Vorgang: dort
   *  beginnen Notiz und Dateien offen (Aufgabe 26, Fix-Runde 1). Weglassen = Listenzeile. */
  standalone?: boolean;
};

/**
 * Das Präfix der Feld-Kennungen dieser Maske — einmal benannt, weil daraus auch das Ziel
 * des Auto-Aufklappens gebildet wird (`zeigeAuftragsFeld`).
 */
const BEARBEITEN_PRAEFIX = "kauf-bearbeiten";

/** Die Beschriftungen je SERVER-Feldname für die Sammelmeldung (siehe
 *  `VORGANG_BESCHRIFTUNGEN` am Angebot) — nie den technischen Namen. */
export const AUFTRAG_BESCHRIFTUNGEN: Readonly<Record<string, string>> = {
  ...VORGANG_BESCHRIFTUNGEN,
  orderedOn: "Bestellt am", customerPurchaseOrderNumber: "Kunden-Bestellnummer",
  paymentTermId: "Zahlungskondition", startDate: "Laufzeitbeginn", endDate: "Laufzeitende",
  openEnded: "Unbefristet",
};

/** Die Felder mit eigenem Fehlerplatz in der Bearbeitungsmaske (siehe Angebot). */
const AUFTRAG_FELDER_AM_FELD: readonly string[] = Object.keys(AUFTRAG_BESCHRIFTUNGEN)
  .filter((f) => !["companyId", "introText", "outroText", "openEnded"].includes(f));

/**
 * Das Auftragsdetail: ein historischer Auftrag (Altbestand ohne Positionen, Beleg und Abrechnung, Aufgabe 26) zeichnet
 * sein eigenes, schmales Detail mit „Bearbeiten“ als einziger Aktion; jeder andere die volle Maske. Die Art eines
 * Vorgangs wechselt nie — die Weiche hängt keinen Zustand um.
 */
export function AuftragDetail(p: AuftragDetailProps): ReactElement {
  if (p.auftrag.kind === "HISTORICAL") {
    return (
      <HistoricalDetail client={p.client} kundeId={p.kundeId} art="order" record={p.auftrag} companies={p.companies}
        umfeld={p.umfeld} bearbeitung={p.bearbeitung} gesperrt={p.gesperrt} onBearbeiten={p.onBearbeiten}
        onSchliessen={p.onSchliessen} onNeuLaden={p.onNeuLaden} onOrdnerOeffnen={p.onOrdnerOeffnen}
        standalone={p.standalone === true} />
    );
  }
  return <OrderDocumentDetail {...p} />;
}

/** Die 422-Codes von „Rechnung erzeugen“, nach denen die Maske die Vorschau neu lädt (Spec 2026-11-02, P12): Inzwischen
 *  ist ein Posten entstanden („Entscheidung fehlt“), ein anderer Entwurf hat die Posten genommen, oder das gewählte Jahr
 *  ist nicht mehr wählbar (Auftrag inzwischen storniert oder umdatiert — Review Aufgabe 4, H2). */
const PREVIEW_RELOAD_CODES: readonly string[] =
  ["PLANNED_CHARGES_DECISION_REQUIRED", "POSTEN_RESERVIERT", "PERIOD_INVALID"];

function OrderDocumentDetail(p: AuftragDetailProps): ReactElement {
  const { client, kundeId, auftrag, companies, umfeld, bearbeitung, gesperrt } = p;

  const [entwurf, setEntwurf] = useState<AuftragEntwurf>(() => auftragEntwurfAus(auftrag));
  /**
   * Die Faltungen je Abschnitt beim BEARBEITEN (`AUFTRAG_START_BEARBEITEN`): alles zu außer den Positionen — dort findet
   * die Arbeit statt (Entscheid Dirk 08.09.2026). Der Start richtet sich nach dem GESPEICHERTEN Vorgang.
   */
  const faltungen = useSectionFolds(AUFTRAG_START_BEARBEITEN(auftragEntwurfAus(auftrag)));
  /**
   * Die Faltungen der LESENDEN Abschnitte im geschlossenen Detail (Gesamtreview, I2): Sie hängen ihren Inhalt erst beim
   * Öffnen ein — `Zahlplan`/`GeplanteFaelligkeiten`, `AbrechnungsquellenKasten`, `Abrechnung`/`AuftragsVolumen` und
   * `Anhaenge` rufen in ihrem Effekt sofort ab, sobald sie gezeichnet werden; bei vielen Aufträgen in der Liste wäre das
   * ein Abruf je Zeile, den fast niemand ansieht. Ein gestörter Auftrag öffnet seine Abrechnungsquellen vorbelegt.
   */
  const readSections = useSectionFolds(orderStartReadOnly(auftrag.abrechnungsquellenStoerung === true));
  /** Was Parteien, Zahlungsvereinbarung und Anlagen zugeklappt sagen: ihr geladener Stand (die Entwürfe von Parteien
   *  und Zahlplan führen die Griffe als Zustand, `draft`). Die Zahl der Quellen steht in `quellenSicht` — sie folgt
   *  jedem Schreiben des Kastens und veraltet deshalb nicht. */
  const loaded = useLoadedState();
  /** Der Abschnitt „Dateien“ — im Panel wie im geschlossenen Detail derselbe Baustein, immer bedienbar; die Dateien des
   *  Herkunftsangebots zeigt er lesend mit (F6/F7). */
  const files: FilesSource = {
    client, kundeId, bezug: { art: "orders", id: auftrag.id }, firmen: companies,
    vorgangName: ROW_TEXT.firstLink("SALES_ORDER", auftrag.displayNumber), lesend: null,
    onOrdnerOeffnen: p.onOrdnerOeffnen,
  };
  /** Die zuletzt geladene Sicht — sie macht aus der Störungszeile den genauen Satz. */
  const [quellenSicht, setQuellenSicht] = useState<Abrechnungsquellen | null>(null);
  /** Gestört? Mit geladener Sicht aus ihr, sonst aus dem Listenfeld (Ruling R-FLAG).
   *  Die Sicht zählt nur, solange sie zur Auftragsversion PASST: Ein Speichern bei
   *  geschlossenem Kasten (Zahlplan, Status) dreht die Version weiter, und der Rahmen
   *  bringt mit dem frischen Auftrag das frische Listenfeld — eine alte Sicht darf es
   *  dann nicht überstimmen. Jede Sicht aus dem Kasten schreibt ihre Version über
   *  `quellenGeladen` zurück, passt also, solange nichts anderes geschehen ist. */
  const quellenSichtGilt = quellenSicht !== null && quellenSicht.version === auftrag.version;
  const quellenStoerung = quellenSichtGilt
    ? hatQuellenStoerung(quellenSicht) : auftrag.abrechnungsquellenStoerung === true;
  const quellenPraefix = `kauf-quellen-${auftrag.id}`;
  /** Steht das Vorschlags-Modal offen? (Paket Teilrechnungen, Knopf „Rechnung
   *  vorschlagen".) */
  const [vorschlagOffen, setVorschlagOffen] = useState(false);
  /** Die Stichtag-Vorgabe des Vorschlags, wenn ihn Weg (b) der Rückfrage geöffnet hat (P13); sonst heute. */
  const [vorschlagStichtag, setVorschlagStichtag] = useState<string | undefined>(undefined);
  /** Die Rückfrage vor „Rechnung erzeugen“ bei offenen Zahlplan-Posten (Spec 2026-11-02): die Vorschau und ein Satz
   *  darüber (nach einer 422 oder 409). `null` = keine Frage offen. */
  const [frage, setFrage] = useState<{ preview: InvoiceFromOrderPreview; notice: string | null } | null>(null);
  /** Nach (a): Quittung und Sprung in den Entwurf — erst, wenn die Frage zu ist (`quittung()` legte sich sonst in sie
   *  und hinge mit ihr aus, Muster LiquidityArea). */
  const [nachFrage, setNachFrage] = useState<{ text: string; rechnung: Rechnung } | null>(null);
  /** Läuft die Vorschau-Abfrage von „Rechnung erzeugen“? Riegel gegen den zweiten Klick, ohne den Knopf abzuschalten. */
  const vorschauLaeuft = useRef(false);
  /** Der Befund der MASKENprüfung (`pruefeAuftrag`) — am Feld UND am Fuss. Befunde des
   *  Servers führt der Formularzustand. */
  const [pruefung, setPruefung] = useState<AuftragBefund | null>(null);
  /** Der Positionsstand des Vorgangs — der Editor meldet ihn nach jedem Schritt. */
  const [positionen, setPositionen] = useState<PositionsStand | null>(null);
  /** Läuft gerade eine Aktion an DIESEM Vorgang (sperrt seine Knöpfe)? */
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  /** Die Rückmeldung des „Als PDF"-Knopfes — MIT ihrer Art (siehe PDF_HINWEIS_ART). */
  const [pdfMeldung, setPdfMeldung] = useState<PdfMeldung | null>(null);
  /** Ist die Vorschau offen? Sie hält keinen eigenen Stand über das Schliessen hinaus —
   *  ein `boolean` genügt, die Bilder holt sie sich selbst (Spec). */
  const [vorschauOffen, setVorschauOffen] = useState(false);
  /** Der Entwurf des Abschnitts PARTEIEN — Bauart und Begründung bei `useParteienGriff`
   *  (vorgangsdetail.ts). */
  const parteien = useParteienGriff();
  /** Der Entwurf des Abschnitts ZAHLUNGSVEREINBARUNG — DRITTER Speicherweg
   *  (`useZahlplanGriff`, vorgangsdetail.ts). */
  const zahlplan = useZahlplanGriff();
  /** Zählt hoch, wenn „Neu laden“ den Serverstand holt — im `key` von Editor, Parteien,
   *  Zahlplan und Anhängen: Sie führen ihren Stand selbst und laden so frisch. */
  const [standNeuLaden, setStandNeuLaden] = useState(0);
  /** Der Speicherweg im Formularzustand (Kopf → Parteien → Zahlplan). */
  const weg = useRef<() => Promise<boolean>>(async () => true);
  /**
   * Die Formularzustände (ME-Etappe 3, Aufgabe 4) — dieselbe Bauart wie am Angebot: Eine
   * 409 setzt den „fremden Stand“ (Eingaben bleiben lesbar, „Neu laden“), statt die Maske zu
   * schliessen und still nachzuladen; eine 422 legt die Befunde an die Felder und springt
   * zum ersten (sein Abschnitt klappt im selben Zug auf).
   */
  const formular = useFormularZustand<boolean>({
    speichern: () => weg.current(),
    neuLaden: async () => {
      const frisch = await client.getSalesOrder(kundeId, auftrag.id);
      p.onFortschreiben(frisch);
      setEntwurf(auftragEntwurfAus(frisch));
      setPruefung(null);
      setPositionen(null);
      parteien.zuruecksetzen();
      zahlplan.zuruecksetzen();
      setStandNeuLaden((n) => n + 1);
    },
    bekannteFelder: AUFTRAG_FELDER_AM_FELD,
    beschriftungen: AUFTRAG_BESCHRIFTUNGEN,
  });
  const speichert = formular.laeuft;

  /**
   * Der Übergang „schliesst auf": Beim Öffnen der Bearbeitung wird der Entwurf frisch aus
   * dem Vorgang gebildet, und der Stand des vorigen Anlaufs verfällt. Bewusst WÄHREND des
   * Zeichnens statt in einem Effekt (React: „Adjusting state when a prop changes") — ein
   * Effekt liefe erst nach dem ersten Bild. `setPositionen(null)` gehört dazu, sonst spränge
   * der Nettobetrag in die Nur-Lese-Fassung, bevor der Editor geladen hat.
   */
  const [zuletztBearbeitung, setZuletztBearbeitung] = useState(bearbeitung);
  if (bearbeitung !== zuletztBearbeitung) {
    setZuletztBearbeitung(bearbeitung);
    // Der geladene Stand gilt nur für den Ort, an dem er geladen wurde (`useLoadedState`) — in BEIDE Richtungen: Nach
    // dem Speichern im Panel zeigte das Detail sonst die Ratenzahl von vorher.
    loaded.reset();
    if (bearbeitung) {
      setEntwurf(auftragEntwurfAus(auftrag));
      setPruefung(null);
      formular.zuruecksetzen();
      setPositionen(null);
      parteien.zuruecksetzen();
      zahlplan.zuruecksetzen();
      // Und die Faltungen mit: Wer ein Panel neu aufschlägt, findet die Abschnitte im
      // Startzustand vor — nicht so, wie er sie beim vorigen Vorgang hinterlassen hat.
      faltungen.resetAll();
      // Die Vorschau schliesst, wenn die Bearbeitung von AUSSEN beginnt (M5, Schlussrunde):
      // Ihr Schleier liegt bei z-index 60, das Vorschau-Modal bei 100 — ohne diese Zeile
      // läge das Bearbeiten-Panel HINTER der Vorschau, statt sie abzulösen.
      setVorschauOffen(false);
    }
  }

  /**
   * Der Editor meldet nach jedem Schritt die frische Version des VORGANGS und den ganzen
   * Stand. Beides muss ankommen: Die Version geht in das nächste `updateSalesOrder` (sonst
   * 409), und aus dem Stand liest die Maske den berechneten Betrag. Und zwar bis in die
   * LISTE des Rahmens (`onFortschreiben`) — dieselbe Begründung wie am Angebot: Die
   * Statusknöpfe arbeiten mit der Version aus der Liste, und wer Positionen bearbeitet und
   * dann „Abbrechen" drückt, bekäme sonst beim ersten Klick eine 409 auf eine Änderung, die
   * er selbst gemacht hat.
   */
  function positionenGeaendert(version: number, stand: PositionsStand): void {
    setPositionen(stand);
    p.onFortschreiben({ ...auftrag, version, netAmount: stand.netAmount });
    setEntwurf((v) => (
      // Sobald Positionen da sind, ist der Betrag berechnet — das Anzeigefeld führt genau
      // den Wert, den `updateSalesOrder` unverändert zurückerwartet.
      stand.items.length === 0 ? v : { ...v, netAmount: betragText(stand.netAmount) }));
  }

  /** Hat der Vorgang Positionen? Entschieden an der ANZAHL, nicht am Betrag: Ein Beleg aus
   *  lauter Zugaben zu 0,00 hat Positionen (Fund G1 aus Aufgabe 2). */
  const hatPositionen = positionen !== null && positionen.items.length > 0;

  /**
   * Der zweite Speicherweg im selben Klick — Reihenfolge, Version und Sinn stehen an
   * `speichereParteien` (vorgangsdetail.ts).
   *
   * **Die BESTELLNUMMER gewinnt aus dem Kopfentwurf** (Fixrunde 1, BLOCKER). Der Auftrag
   * führt sie als eigene Spalte, und der Block zeigt sie deshalb gar nicht — seinen
   * Entwurf trägt sie trotzdem, mit dem Wert VOM LADEN. `setBelegParteien` verlangt das
   * Feld im Rumpf (`null` heisst gelöscht, weglassen geht nicht), und der Schreibweg
   * synchronisiert es in die Spalte zurück. Ohne diese Übersteuerung überschriebe der
   * zweite Weg still die Nummer, die `updateSalesOrder` eine Zeile vorher gespeichert und
   * durchgeschrieben hat — die Eingabe wäre weg, ohne eine einzige Meldung.
   */
  function parteienSpeichern(
    frisch: Auftrag, folgefehler: { e: unknown },
  ): Promise<number | null> {
    let version: number | null = null;
    return speichereParteien({
      client, art: "order", customerId: auftrag.customerId, vorgangId: auftrag.id,
      version: frisch.version, griff: parteien,
      ueberschreibt: {
        purchaseOrderNumber: oderNull(entwurf.customerPurchaseOrderNumber),
      },
      beiFehler: (meldung, fehler) => {
        // Der frische Vorgang muss in die Liste des Rahmens und in den Entwurf — sonst
        // rechnete der nächste Anlauf mit der alten Version und bekäme eine 409 über eine
        // Änderung, die niemand fremd gemacht hat.
        p.onFortschreiben(frisch);
        setEntwurf(auftragEntwurfAus(frisch));
        folgefehler.e = folgewegFehler(meldung, fehler);
        zeigeAuftragsFehlerFeld(faltungen, BEARBEITEN_PRAEFIX, fehler);
      },
      beiErfolg: (neueVersion) => { version = neueVersion; },
    }).then((ok) => (ok ? version ?? frisch.version : null));
  }

  /** Der DRITTE Speicherweg — die Zahlungsvereinbarung. Kopf → Parteien → Zahlplan; die
   *  Version ist die aus der ANTWORT des Parteien-Weges (`speichereZahlplan`,
   *  vorgangsdetail.ts). */
  function zahlplanSpeichern(version: number, folgefehler: { e: unknown }): Promise<boolean> {
    return speichereZahlplan({
      client, bezug: { art: "order", customerId: auftrag.customerId,
                       vorgangId: auftrag.id },
      version, griff: zahlplan,
      beiFehler: (meldung, fehler) => {
        folgefehler.e = folgewegFehler(meldung, fehler);
        zeigeAuftragsFehlerFeld(faltungen, BEARBEITEN_PRAEFIX, fehler);
      },
    });
  }

  async function speichern(): Promise<void> {
    const problem = pruefeAuftrag(entwurf, false);
    if (problem !== null) {
      setPruefung(problem);
      // Der Befund betrifft immer ein Feld der Kopfdaten — die beim Bearbeiten zugeklappt
      // beginnen. Ohne dieses Aufklappen stünde die Meldung über einem Formular, in dem das
      // gemeinte Feld gar nicht zu sehen ist: ein unsichtbarer Fehler.
      zeigeAuftragsFeld(faltungen, BEARBEITEN_PRAEFIX, problem.feld);
      return;
    }
    setPruefung(null);
    weg.current = kopfSpeichern;
    if (await formular.aktion() !== true) return;
    p.onSchliessen();
    await p.onNeuLaden();
  }

  /**
   * Der Speicherweg im Formularzustand: Kopf → Parteien → Zahlplan. Jeder Weg dreht die
   * Belegversion weiter; der nächste bekommt die aus der ANTWORT des vorigen — der Zahlplan
   * steht zuletzt, weil er der einzige Weg ist, der die geplanten Fälligkeiten neu rechnet.
   *
   * version = die gelesene Version: ein zwischenzeitlich fremd geänderter Satz ergibt 409
   * statt einer stillen Überschreibung. BINDEND FÜR VERWENDER: Die Liste darf nicht neu
   * laden, solange ein Detail in Bearbeitung ist (die Version kommt aus der Liste des
   * Rahmens).
   */
  async function kopfSpeichern(): Promise<boolean> {
    let frisch: Auftrag;
    try {
      frisch = await client.updateSalesOrder(
        kundeId, auftrag.id, auftrag.version, auftragKopffelder(entwurf));
    } catch (e) {
      // Die zweite Quelle von Feldbefunden: Der Server prüft mehr als die Maske — vor
      // allem die Laufzeit-Konsistenz („Das Enddatum darf nicht vor dem Startdatum
      // liegen"). Steht das Feld in einem zugeklappten Abschnitt, geht er im SELBEN Zug auf,
      // in dem der Befund ans Feld kommt.
      openSectionsWithFindings(faltungen, AUFTRAG_ABSCHNITTSFELDER, e);
      throw e;
    }
    const folgefehler: { e: unknown } = { e: null };
    const parteienVersion = await parteienSpeichern(frisch, folgefehler);
    if (parteienVersion === null) throw folgefehler.e;
    if (!await zahlplanSpeichern(parteienVersion, folgefehler)) throw folgefehler.e;
    return true;
  }

  const { bestaetigen } = useBestaetigung();

  /**
   * Die allgemeine Rückfrage vor „Rechnung erzeugen“ (Entscheid 682) — nur noch ohne offene Zahlplan-Posten; mit ihnen
   * stellt `rechnungErzeugen` statt ihrer die Zahlplan-Rückfrage (Spec 2026-11-02), nie beide hintereinander.
   */
  async function rechnungErzeugenBestaetigt(): Promise<boolean> {
    return await bestaetigen(CREATE_INVOICE_QUESTION(auftrag.displayNumber));
  }

  /**
   * „Rechnung erzeugen" — die Ableitung aus dem Auftrag (Paket 3b-2). Der Server kopiert
   * Kopf, Texte, Leistungszeitraum, den Zahlungssatz-Snapshot und die Positionen; hier wird
   * nichts zusammengestellt. Mehrere Rechnungen je Auftrag sind ausdrücklich erlaubt
   * (Abschlags-/Schlussrechnung), deshalb steht der Knopf auch an einem Auftrag, der schon
   * eine hat — und in JEDEM Status: Auch zu einem abgeschlossenen Auftrag darf eine
   * Nachrechnung entstehen.
   *
   * Ablauf P12 (Spec 2026-11-02 „Rechnung erzeugen an Aufträgen mit Zahlplan“): Der Klick liest zuerst die Vorschau.
   * Ohne offene Posten in wählbaren Jahren bleibt es bei der allgemeinen Rückfrage (Entscheid 682) und dem POST ohne
   * Rumpf. Mit offenen Posten stellt sich STATT ihrer die Zahlplan-Rückfrage — eine Frage je Klick, nie zwei
   * hintereinander. Still entsteht nie eine ungebundene Vollkopie: Die 422 „Entscheidung fehlt“ bzw. „inzwischen
   * vergeben“ lädt die Vorschau neu und fragt (wieder), statt ohne Rumpf zu wiederholen.
   *
   * Die Vorschau sperrt den Knopf NICHT (`beschaeftigt` bleibt den Schreibwegen): Ein abgeschalteter Knopf verliert in
   * Chromium und WebKit den Fokus an <body> (Fokus-Fixup), und die Frage gäbe ihn beim Schliessen nirgends zurück —
   * nach Escape stünde ein Tastaturbenutzer am Seitenanfang. Den Doppelklick fängt `vorschauLaeuft` ab.
   */
  async function rechnungErzeugen(): Promise<void> {
    if (p.onRechnungAusAuftrag === undefined || vorschauLaeuft.current) return;
    vorschauLaeuft.current = true;
    p.onHinweis(null);
    let preview: InvoiceFromOrderPreview;
    try {
      preview = await client.getInvoiceFromOrderPreview(kundeId, auftrag.id);
    } catch (e) {
      p.onHinweis(apiMeldung(e, INVOICE_FROM_ORDER_WEB_TEXT.failed));
      return;
    } finally {
      vorschauLaeuft.current = false;
    }
    if (preview.periods.length > 0) {
      setFrage({ preview, notice: null });
      return;
    }
    if (!(await rechnungErzeugenBestaetigt())) return;
    await erzeugen(null);
  }

  /**
   * Der POST „Rechnung erzeugen“: ohne Wahl ohne Rumpf, mit Wahl (a) für das Leistungsjahr ab `periodStart`. Nach (a)
   * schliesst die Frage, dann folgen Quittung und Sprung (`nachFrage`). Eine 422 der Neulade-Codes (`PREVIEW_RELOAD_CODES`,
   * seit Review 4 H2 auch PERIOD_INVALID) lädt die Vorschau neu; jeder andere Fehler (409 Sperrkollision, Netz) steht wie
   * an den übrigen Wegen am Auftrag, und eine offene Frage bleibt offen — mit dem Satz darüber, denn der Meldungsstreifen
   * liegt unter ihr.
   */
  async function erzeugen(periodStart: string | null): Promise<void> {
    const melden = p.onRechnungAusAuftrag;
    if (melden === undefined) return;
    setBeschaeftigt(true);
    p.onHinweis(null);
    try {
      if (periodStart === null) {
        melden(await client.createInvoiceFromOrder(kundeId, auftrag.id));
        return;
      }
      const rechnung = await client.createInvoiceFromOrder(kundeId, auftrag.id,
        { replacePlannedCharges: { periodStart } });
      const periode = frage?.preview.periods.find((x) => x.periodStart === periodStart);
      setFrage(null);
      setNachFrage({ text: periode === undefined ? "" : INVOICE_FROM_ORDER_WEB_TEXT.receipt(periode), rechnung });
    } catch (e) {
      if (e instanceof ApiError && e.status === 422 && PREVIEW_RELOAD_CODES.includes(e.code)) {
        await neuFragen(e);
        return;
      }
      const meldung = apiMeldung(e, INVOICE_FROM_ORDER_WEB_TEXT.failed);
      p.onHinweis(meldung);
      setFrage((offen) => (offen === null ? null : { ...offen, notice: meldung }));
    } finally {
      setBeschaeftigt(false);
    }
  }

  /** Nach einer 422 der Neulade-Codes: Vorschau neu lesen. Leer → Frage zu, Hinweis „inzwischen vergeben“; sonst die
   *  Frage (wieder) mit dem Satz zum Code darüber. */
  async function neuFragen(fehler: ApiError): Promise<void> {
    let preview: InvoiceFromOrderPreview;
    try {
      preview = await client.getInvoiceFromOrderPreview(kundeId, auftrag.id);
    } catch (e) {
      setFrage(null);
      p.onHinweis(apiMeldung(e, INVOICE_FROM_ORDER_WEB_TEXT.failed));
      return;
    }
    // PERIOD_INVALID hat keinen eigenen Satz der Oberfläche: Es gilt der des Servers.
    const satz = fehler.code === "PERIOD_INVALID" ? apiMeldung(fehler, INVOICE_FROM_ORDER_WEB_TEXT.failed) : null;
    if (preview.periods.length === 0) {
      setFrage(null);
      p.onHinweis(satz ?? INVOICE_FROM_ORDER_WEB_TEXT.chargesTaken);
      return;
    }
    setFrage({ preview, notice: satz ?? (fehler.code === "POSTEN_RESERVIERT"
      ? INVOICE_FROM_ORDER_WEB_TEXT.chargesTaken : INVOICE_FROM_ORDER_WEB_TEXT.decisionRequired) });
  }

  /** (b) „Stattdessen vorschlagen“: Frage zu, Vorschlag mit dem Stichtag des frühesten offenen Postens (P13). */
  function stattdessenVorschlagen(preview: InvoiceFromOrderPreview): void {
    setFrage(null);
    setVorschlagStichtag(proposalCutoff(preview, defaultCutoffDate()));
    setVorschlagOffen(true);
  }

  // Quittung und Sprung nach (a), sobald die Frage ausgehängt ist (siehe `nachFrage`).
  useEffect(() => {
    if (frage !== null || nachFrage === null) return;
    setNachFrage(null);
    if (nachFrage.text !== "") quittung({ text: nachFrage.text });
    p.onRechnungAusAuftrag?.(nachFrage.rechnung);
  }, [frage, nachFrage]);

  async function statusSetzen(status: OrderStatusTarget): Promise<void> {
    // Erst die Rückfrage (Hausregel „Bestätigung vor Datenänderung“, 19.10.2026): Abgeschlossen und Storniert sind
    // Endzustände ohne Rückgängig.
    if (!(await bestaetigen(STATUS_CHANGE_TEXT.order[status](auftrag.displayNumber)))) return;
    setBeschaeftigt(true);
    p.onHinweis(null);
    try {
      await client.setSalesOrderStatus(kundeId, auftrag.id, auftrag.version, status);
      await p.onNeuLaden();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        p.onHinweis("Dieser Auftrag wurde zwischenzeitlich geändert. Der aktuelle Stand ist "
          + "neu geladen.");
        await p.onNeuLaden();
      } else {
        p.onHinweis(apiMeldung(e, "Der Status konnte nicht geändert werden."));
      }
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * „Als PDF" (E3-T4): Die Engine setzt die AUFTRAGSBESTÄTIGUNG zum Auftrag, die API
   * liefert den Download durch, hier wird er gespeichert. Die drei Ausgänge tragen ihre
   * ART mit (PDF_HINWEIS_ART): Erfolg als Status, die 501 der unkonfigurierten
   * Umgebung als HINWEIS (bekannter Arbeitsstand, kein Alarm), alles andere als Alarm.
   */
  async function alsPdf(): Promise<void> {
    setBeschaeftigt(true);
    setPdfMeldung(null);
    try {
      const { blob, filename } = await client.renderOrder(kundeId, auftrag.id);
      const name = filename ?? "auftragsbestaetigung.pdf";
      speichereBlob(blob, name);
      setPdfMeldung({ text: `Das PDF wurde heruntergeladen (${name}).`, art: "gut" });
    } catch (e) {
      if (e instanceof ApiError && e.status === 501) {
        setPdfMeldung({
          text: "Die Render-Engine ist noch nicht angebunden — es entsteht noch kein PDF.",
          art: "hinweis",
        });
      } else {
        // Nennt die Engine Feldpfade, reisen sie strukturiert mit (`fields[]`, an JEDEM
        // Engine-Fehlschlag) und werden unten als Liste gezeigt. Der Schlüssel entfällt,
        // wenn keine kamen — `exactOptionalPropertyTypes`.
        setPdfMeldung({
          text: apiMeldung(e, "Der Auftrag konnte nicht als PDF ausgegeben werden."),
          art: "schlecht",
          ...(e instanceof ApiError && e.fields !== undefined ? { felder: e.fields } : {}),
        });
      }
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * Trägt das Panel ungespeicherte Eingaben? Der Abschnitt PARTEIEN zählt mit: Er meldet
   * seinen Entwurf erst, sobald ihn jemand angefasst hat — und genau dann gibt es etwas zu
   * verlieren.
   */
  const ungespeichert = bearbeitung
    && (!entwuerfeGleich(entwurf, auftragEntwurfAus(auftrag))
      || parteien.beruehrt || zahlplan.beruehrt);

  /* Der Wächter gegen das Schliessen des BROWSERS (Global Constraint „kein stiller
     Datenverlust"): Der Abbrechen-Wächter fängt nur den Weg über den Knopf und Escape;
     ein geschlossener Reiter ginge an ihm vorbei. */
  useBeforeUnload(ungespeichert);

  const folgestatus = erlaubteFolgestatus(auftrag.status);
  const kuerzel = companies.find((c) => c.id === auftrag.companyId)?.shortCode ?? null;

  /**
   * Der EINE Ausgang des Bearbeitungspanels neben dem Speichern (Hausregel „striktes
   * Modalpanel", 06.09.2026) — dieselbe Bauart wie am Angebot: kein Ausgang unter laufendem
   * Schreiben, Rückfrage bei ungespeicherten Kopfeingaben, verglichen gegen den Vorgang, wie
   * er GERADE steht (der Positions-Editor dreht ihn selbst weiter). Die Positionen stehen
   * nie ungespeichert im Panel — der Editor schreibt jeden Schritt sofort.
   */
  async function verlassen(): Promise<boolean> {
    if (speichert) return false;
    if (!(await darfAbbrechen(ungespeichert))) return false;
    parteien.zuruecksetzen();
    zahlplan.zuruecksetzen();
    formular.zuruecksetzen();
    p.onSchliessen();
    return true;
  }
  async function abbrechen(): Promise<void> { await verlassen(); }
  /** „Ordner“ im Panel geht durch denselben Ausgang (Fix-Runde 1 zu Aufgabe 25), erst dann in den Reiter Dateien. */
  const folderFromPanel = useJumpAfterLeaving(bearbeitung, verlassen, p.onOrdnerOeffnen);

  /** Befunde je Feld: die des Servers, die der Maskenprüfung und — für den Fehlerzähler
   *  je Abschnitt — die der beiden Folgewege (sie stehen in ihren Abschnitten). */
  const fehlerJeFeld: Record<string, string> = {
    ...formular.feldFehler,
    ...(pruefung !== null ? { [pruefung.feld]: pruefung.meldung } : {}),
    ...parteien.befunde, ...zahlplan.befunde,
  };

  /** Der Text der Hinweiszeile „fremder Stand“ (Slot `hinweis` des Formulars). */
  function fremderStandHinweis(): ReactNode {
    if (formular.zustand !== "fremder-stand") return undefined;
    const wer = formular.fremderStand?.changedBy;
    return (
      <>
        {wer !== undefined && wer !== ""
          ? `${wer} hat diesen Auftrag inzwischen geändert.`
          : "Dieser Auftrag wurde inzwischen geändert."}
        {" Deine Eingaben stehen noch da — „Neu laden“ holt den aktuellen Stand und "
          + "ersetzt sie."}
      </>
    );
  }

  /** Der Sprung in den Kasten — aus der Störungszeile und aus dem Rechnungsvorschlag.
   *  `zeigeFeld` klappt auf und fokussiert danach den Container (tabIndex -1). */
  function zuDenQuellen(): void {
    readSections.billingSources.zeigeFeld(`${quellenPraefix}-kasten`);
  }

  /**
   * Eine neue Sicht aus dem Kasten (Fixrunde A10-1, Controller-Ruling zur Version):
   *
   * - Nach dem EIGENEN erfolgreichen Speichern (`ausSchreiben`) stempelt der Rahmen die neue
   *   Auftragsversion samt Störung in seine Liste — sonst liefe das nächste Speichern in eine
   *   409, und der Listen-Chip stünde still falsch. Das ist sicher: Der PUT ging gegen genau
   *   die Version, die der Rahmen kennt (der Kasten schickt `version={auftrag.version}`,
   *   nicht die seiner Sicht — Fixrunde A10-2), dazwischen kann niemand etwas geändert haben.
   *   Nach einer 409 bleibt das offene Modal stehen: Beide Rahmen (KundeAuftraege,
   *   BereichAuftraege) laden neu, ohne das Detail neu einzuhängen (Schlüssel = Auftrags-ID,
   *   kein Zwischenstand „Lädt"); der Kasten lädt mit der neuen Version nur seine Sicht nach.
   * - Eine GELESENE oder aus einem Konflikt stammende Sicht mit ANDERER Version heisst:
   *   Jemand anderes hat den Auftrag geändert. Ihre Version auf die alten Felder zu stempeln
   *   hiesse, das nächste „Bearbeiten → Speichern" überschriebe still die fremde Änderung.
   *   Deshalb lädt der Rahmen neu (`onNeuLaden`); bis dahin gilt das Listenfeld
   *   (`quellenSichtGilt` ist falsch).
   * - Eine gelesene Sicht mit GLEICHER Version darf nur das Störungsfeld nachführen (keine
   *   Version) — und nur bei einer Änderung, sonst zeichnete jedes Laden neu.
   */
  function quellenGeladen(s: Abrechnungsquellen, herkunft?: { ausSchreiben: true }): void {
    setQuellenSicht(s);
    const stoerung = hatQuellenStoerung(s);
    const stoerungAlt = auftrag.abrechnungsquellenStoerung === true;
    if (herkunft?.ausSchreiben === true) {
      if (s.version !== auftrag.version || stoerung !== stoerungAlt) {
        p.onFortschreiben({ ...auftrag, version: s.version, abrechnungsquellenStoerung: stoerung });
      }
      return;
    }
    if (s.version !== auftrag.version) {
      // Ein fehlschlagendes Neuladen meldet der Rahmen selbst; hier bleibt nur, dass es
      // nicht als unbehandelte Ablehnung durchschlägt.
      void Promise.resolve(p.onNeuLaden()).catch(() => undefined);
      return;
    }
    if (stoerung !== stoerungAlt) {
      p.onFortschreiben({ ...auftrag, abrechnungsquellenStoerung: stoerung });
    }
  }
  /**
   * Die Inhalte der Kästen, die im Panel UND im geschlossenen Detail stehen — einmal gebaut, an beiden Orten nur hinter
   * `offen` gezeichnet (kein Abruf auf Verdacht, W35). Ihre Zähler kommen aus dem ersten Laden am jeweiligen Ort.
   *
   * Abrechnungsquellen und Anhänge schreiben selbst und drehen dabei `auftrag.version` weiter: Der Kasten schickt
   * `version={auftrag.version}`, und `quellenGeladen` bzw. `onVersionChange` tragen die neue über den Rahmen zurück —
   * ohne das liefe das nächste Speichern in eine 409 für eine Änderung, die niemand fremd gemacht hat.
   */
  const sourceCount = quellenSicht?.quellen.length;
  const billingSourcesBox = (open: boolean): ReactNode => open && (
    <AbrechnungsquellenKasten client={client} customerId={kundeId}
      orderId={auftrag.id} version={auftrag.version} idPraefix={quellenPraefix}
      onSicht={quellenGeladen} istAdmin={p.istAdmin === true} />
  );
  const billingBoxes = (open: boolean): ReactNode => open && (
    <>
      <Abrechnung client={client} customerId={kundeId} orderId={auftrag.id} currency={auftrag.currency} />
      <AuftragsVolumen client={client} customerId={kundeId} orderId={auftrag.id} currency={auftrag.currency} />
    </>
  );
  /** Die Anlagen aus Textbausteinen — am Auftrag NUR lesend, mit dem Ausblenden-Schalter je Anlage (Spec, Abschnitt
   *  Oberfläche): „kein Anhängen, kein Umsortieren, kein Löschen — die Anhänge kommen aus der Ableitung." Der Kasten wird
   *  das SELBST, weil sein Bezug `art: "order"` ist (components/Anhaenge.tsx); ein `nurLesend` von hier schaltete den
   *  Ausblenden-Schalter mit ab. Der Schalter wirkt SOFORT auf den Server (Hausregel-Ausnahme, Kopfkommentar dort). */
  const attachmentsBox = (open: boolean, idPrefix: string, covered?: boolean): ReactNode => open && (
    <Anhaenge key={`${auftrag.id}-anhaenge-${String(standNeuLaden)}`} client={client}
      bezug={{ art: "order", customerId: kundeId, vorgangId: auftrag.id }}
      idPraefix={idPrefix} {...(covered === undefined ? {} : { verdeckt: covered })}
      version={auftrag.version}
      onVersionChange={(version) => { p.onFortschreiben({ ...auftrag, version }); }}
      onLoaded={loaded.onAttachmentsLoaded} />
  );

  /** Die Laufzeit-Ende-Angabe: „unbefristet", ein Enddatum oder nichts. */
  const ende = auftrag.openEnded
    ? "unbefristet"
    : (auftrag.endDate !== null ? `läuft bis ${formatDatum(auftrag.endDate)}` : null);

  return (
    <>
      <div className="kauf__zeile">
        <span className="kauf__nummer">{auftrag.displayNumber}</span>
        {kuerzel !== null && <Chip {...hausfarbeChip(companies, kuerzel)}>{kuerzel}</Chip>}
        <span className="kauf__eintrag-titel">{auftrag.title}</span>
        {/* Schlechtestes oben (Hausregel): Der Chip steht VOR dem Statuschip. Beide sind Kennzeichen (`me-chip`, Entscheid 671), der
            Status in der Art wie in der Bereichsliste (AUFTRAG_STATUS_ART). */}
        {quellenStoerung && <Chip className="kauf__quellenchip" statusArt="fehler">Quelle fehlt</Chip>}
        <Chip className="kauf__status" statusArt={AUFTRAG_STATUS_ART[auftrag.status]}>{AUFTRAG_STATUS_LABEL[auftrag.status]}</Chip>
      </div>

      <div className="kauf__daten">
        <span className="kauf__betrag ki-num">
          {formatBetrag(auftrag.netAmount, auftrag.currency)}
        </span>
        {ende !== null && <span className="kauf__laufzeit">{ende}</span>}
        {auftrag.sourceOfferNumber !== null && (
          <span className="kauf__herkunft">aus Angebot {auftrag.sourceOfferNumber}</span>
        )}
        {auftrag.contactName !== null && auftrag.contactName !== "" && (
          <span className="kauf__kontakt">{auftrag.contactName}</span>
        )}
      </div>

      {/* Die PDF-Rückmeldung steht AM VORGANG, zu dem sie gehört: Der Baustein trägt sie
          samt ihrer Befundliste selbst, damit sie auch dort erscheint, wo kein Reiter
          darüber liegt (der Auftrags-Bereich, views/BereichAuftraege.tsx). */}
      {pdfMeldung !== null && (
        <Hinweis art={PDF_HINWEIS_ART[pdfMeldung.art]}>{pdfMeldung.text}</Hinweis>
      )}
      {/* Die Feldbefunde als LISTE — siehe die Begründung in AngebotDetail.tsx: neben der
          Meldung statt in ihr (die trägt `role="alert"` und bleibt ein Satz), und mit
          EIGENEM Blocknamen `kauf__` statt einer über die Ansichten geteilten Klasse. */}
      {pdfMeldung?.felder !== undefined && pdfMeldung.felder.length > 0 && (
        <ul className="kauf__fehlerfelder" aria-label="Fehlende Pflichtwerte">
          {pdfMeldung.felder.map((f) => (
            <li key={f.field}>
              <code>{f.field}</code>: {f.message}
            </li>
          ))}
        </ul>
      )}

      {bearbeitung && (
        <Modal titel={`Auftrag ${auftrag.displayNumber} bearbeiten`} breite="weit"
          onAbbrechen={abbrechen} abbrechenGesperrt={speichert}>
          <div className="kauf__bearbeitung">
            {auftragFormularFelder({
              praefix: BEARBEITEN_PRAEFIX, mitUnternehmen: false, companies, umfeld,
              entwurf, setEntwurf, hatPositionen, currency: auftrag.currency, faltungen, fehlerJeFeld,
              feldFehler: pruefung?.meldung ?? formular.meldung,
              formular: { ...formular.formularProps, hinweis: fremderStandHinweis() },
              vorschauWerte: auftragVorschauWerte({
                entwurf, companies, kontakte: umfeld.kontakte, kundeName: p.kundeName,
              }),
              /* Der Abschnitt PARTEIEN — `key` am Vorgang: Ein Wechsel des bearbeiteten
                 Auftrags baut ihn neu auf, statt den Entwurf des vorigen weiterzureichen.
                 OHNE Bestellnummer: Die führt der Auftrag als eigenes Kopffeld
                 (`customerPurchaseOrderNumber`) in den Kopfdaten. */
              parteien: {
                // Der Käufer des Entwurfs, sobald jemand den Abschnitt angefasst hat, sonst der geladene.
                zusammenfassung: partiesSummary(loaded.parties, parteien.draft?.billingCustomerId, p.kundeName),
                inhalt: (
                  <BelegParteien key={`${auftrag.id}-${String(standNeuLaden)}`}
                    client={client} art="order"
                    customerId={auftrag.customerId} vorgangId={auftrag.id}
                    idPraefix={BEARBEITEN_PRAEFIX} verdeckt={formular.zustand !== "bereit"}
                    mitBestellnummer={false}
                    befunde={parteien.befunde} onEntwurf={parteien.melde} onLoaded={loaded.onPartiesLoaded} />
                ),
              },
              /* Die ZAHLUNGSVEREINBARUNG — `key` am Vorgang aus demselben Grund wie an
                 PARTEIEN. Die geplanten Fälligkeiten (mit „Neu berechnen“) stehen nur im
                 geschlossenen Detail: Im offenen Formular gäbe es sonst einen zweiten
                 Schreibweg neben dem Zahlplan (Ruling 3 zu Aufgabe 9). */
              zahlplan: {
                zusammenfassung: ratesSummary(zahlplan.draft?.raten.length ?? loaded.rateCount),
                inhalt: (
                  <Zahlplan key={`${auftrag.id}-zahlplan-${String(standNeuLaden)}`}
                    client={client}
                    bezug={{ art: "order", customerId: auftrag.customerId,
                             vorgangId: auftrag.id }}
                    idPraefix={BEARBEITEN_PRAEFIX} verdeckt={formular.zustand !== "bereit"}
                    befunde={zahlplan.befunde} onEntwurf={zahlplan.melde}
                    onLoaded={loaded.onRatesLoaded} />
                ),
              },
              billingSources: {
                zaehler: sourceCount, inhalt: billingSourcesBox(faltungen.billingSources.offen),
              },
              billingVolume: { inhalt: billingBoxes(faltungen.billingVolume.offen) },
              /* Der grösste Block, in der Mitte der Maske (Entscheid Dirk 08.09.2026).
                 DERSELBE Editor wie am Angebot — nur `art` unterscheidet die beiden
                 Einsatzorte. Die Positionen hängen an DEMSELBEN Vorgang wie die Kopffelder
                 darüber: Jeder Schritt im Editor dreht die Version des Auftrags weiter, und
                 `positionenGeaendert` trägt sie über den Rahmen zurück — sonst liefe das
                 nächste Speichern in eine 409. */
              positionen: {
                ...itemsHead(positionen, auftrag.currency),
                inhalt: (
                  <div className="kauf__positionen">
                    <PositionsEditor
                      // „Neu laden“ im fremden Stand hängt den Editor neu auf — er führt
                      // seinen Stand selbst und lädt so den Serverstand.
                      key={`${auftrag.id}-${String(standNeuLaden)}`}
                      client={client}
                      bezug={{ art: "order", customerId: kundeId, vorgangId: auftrag.id }}
                      companyId={auftrag.companyId} version={auftrag.version} waehrung={auftrag.currency}
                      gesperrt={formular.zustand !== "bereit"}
                      kontierung={{ client, companyId: auftrag.companyId, art: "ORDER", gesperrt: false }}
                      onVersionChange={positionenGeaendert} />
                  </div>
                ),
              },
              anhaenge: {
                ...counterProp(loaded.attachmentCount),
                inhalt: attachmentsBox(faltungen.textAttachments.offen, BEARBEITEN_PRAEFIX,
                  formular.zustand !== "bereit"),
              },
              dateien: filesContent({ ...files, onOrdnerOeffnen: folderFromPanel }, true, loaded),
            })}
          </div>

          {/* Die zwei einzigen Ausgänge, unten am Panel: Speichern schreibt die KOPFFELDER
              (die Positionen schreibt der Editor Schritt für Schritt selbst). */}
          <ModalFuss speichernText="Speichern"
            speichernGesperrt={formular.zustand !== "bereit"}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}

      {!bearbeitung && (
        <div className="kauf__aktionen">
          <KiKnopf name={`${auftrag.title} bearbeiten`} deaktiviert={gesperrt || beschaeftigt}
            onClick={p.onBearbeiten}>
            Bearbeiten
          </KiKnopf>
          {/* Die Vorschau — DIREKT NEBEN „Als PDF" (Spec, wörtlich). Der Entwurf-Chip der
              Vorschau erscheint hier NIE: Die Engine antwortet an einer Auftragsbestätigung
              immer mit `entwurf: false` (sie kennt keinen Entwurf), und der Baustein liest
              genau dieses Feld — hier steht deshalb KEINE eigene Verzweigung dafür. */}
          <KiKnopf deaktiviert={gesperrt || beschaeftigt}
            onClick={() => setVorschauOffen(true)}>
            Vorschau
          </KiKnopf>
          <KiKnopf deaktiviert={gesperrt || beschaeftigt} onClick={() => { void alsPdf(); }}>
            Als PDF
          </KiKnopf>
          {/* Das LAYOUT der Auftragsbestätigung (Migration 049, Fragen 263, 266) — fest ab Abschluss. */}
          <BelegLayoutKnopf client={client} art="order" customerId={auftrag.customerId}
            vorgangId={auftrag.id} gesperrt={gesperrt || beschaeftigt} version={auftrag.version}
            onNeuLaden={p.onNeuLaden}
            onVersion={(version) => p.onFortschreiben({ ...auftrag, version })} />
          {folgestatus.map((s) => (
            <KiKnopf key={s} deaktiviert={gesperrt || beschaeftigt}
              onClick={() => { void statusSetzen(s); }}>
              {ORDER_STATUS_ACTION[s]}
            </KiKnopf>
          ))}
          {/* „Rechnung vorschlagen" (Paket Teilrechnungen) — nur an einem AKTIVEN Auftrag:
              An einem abgeschlossenen oder stornierten Auftrag entstehen keine weiteren
              geplanten Posten mehr, „Rechnung erzeugen" bleibt dort trotzdem für die
              Nachrechnung (siehe dessen Begründung). Dieselbe Fähigkeitsprüfung wie bei
              „Rechnung erzeugen": Wer die Naht zur Rechnung nicht verdrahtet hat, bekommt
              auch diesen Knopf nicht. */}
          {p.onRechnungAusAuftrag !== undefined && auftrag.status === "ACTIVE" && (
            <KiKnopf deaktiviert={gesperrt || beschaeftigt}
              onClick={() => { setVorschlagStichtag(undefined); setVorschlagOffen(true); }}>
              Rechnung vorschlagen
            </KiKnopf>
          )}
          {p.onRechnungAusAuftrag !== undefined && (
            <KiKnopf art="primaer" deaktiviert={gesperrt || beschaeftigt}
              onClick={() => { void rechnungErzeugen(); }}>
              Rechnung erzeugen
            </KiKnopf>
          )}
        </div>
      )}

      {/* Die RÜCKFRAGE vor „Rechnung erzeugen“ bei offenen Zahlplan-Posten (Spec 2026-11-02): (b) nur am AKTIVEN
          Auftrag, wo es auch „Rechnung vorschlagen“ gibt. */}
      {frage !== null && p.onRechnungAusAuftrag !== undefined && (
        <InvoiceFromOrderQuestion preview={frage.preview} notice={frage.notice} busy={beschaeftigt}
          onCreate={(periodStart) => { void erzeugen(periodStart); }}
          onPropose={auftrag.status === "ACTIVE" ? () => stattdessenVorschlagen(frage.preview) : undefined}
          onCancel={() => { if (!beschaeftigt) setFrage(null); }} />
      )}

      {/* Das VORSCHLAGS-MODAL (Paket Teilrechnungen) — derselbe Sprung wie nach „Rechnung
          erzeugen": Der frische Entwurf geht über `onRechnungAusAuftrag` an den Rahmen,
          der auf den Rechnungen-Reiter wechselt. Ein eigener Naht-Rückruf nur für diesen
          Weg wäre eine zweite Tür in dieselbe Wand. */}
      {vorschlagOffen && p.onRechnungAusAuftrag !== undefined && (
        <Rechnungsvorschlag client={client} customerId={kundeId} orderId={auftrag.id}
          stichtag={vorschlagStichtag}
          onAbbrechen={() => setVorschlagOffen(false)}
          onZuAbrechnungsquellen={() => { setVorschlagOffen(false); zuDenQuellen(); }}
          onFertig={(rechnung) => {
            setVorschlagOffen(false);
            p.onRechnungAusAuftrag?.(rechnung);
          }} />
      )}

      {/* DIE STÖRUNGSZEILE (Spec „Abrechnungsquellen", Oberfläche): über den Kästen, rot,
          mit Sprung in den Kasten. Sie steht nur am AKTIVEN Auftrag (R-STOERUNG). */}
      {!bearbeitung && quellenStoerung && (
        <Hinweis art="fehler" className="kauf__quellenstoerung"
          aktionen={
            <KiKnopf art="leise"
              name={`Zum Kasten Abrechnungsquellen von ${auftrag.displayNumber}`}
              onClick={zuDenQuellen}>
              Zum Kasten
            </KiKnopf>
          }>
          {stoerungsText(quellenSichtGilt ? quellenSicht : null)}
        </Hinweis>
      )}

      {/* Die LESENDEN Abschnitte des geschlossenen Details in der Folge der Spec-Tabelle, alle zu (Abrechnungsquellen
          beim gestörten Auftrag offen) und nur offen gezeichnet (siehe `readSections`). Zahlungsvereinbarung und
          geplante Fälligkeiten stehen in EINEM Abschnitt (Gesamtreview, I2): Eine Vereinbarung ohne ihre Fälligkeiten
          wäre die halbe Auskunft. Der Knopf „Neu berechnen“ nur an einem AKTIVEN Auftrag (Gesamtreview, I1): Der Server
          weist die Neuberechnung sonst mit 422 AUFTRAG_NICHT_AKTIV ab. */}
      {!bearbeitung && (
        <>
          <Vorgangsabschnitt art="abrechnung" titel={SECTION_TEXT.paymentOrder} faltung={readSections.payment}
            inhaltId={`kauf-faelligkeiten-inhalt-${auftrag.id}`}
            {...summaryProp(ratesSummary(loaded.rateCount))}>
            {readSections.payment.offen && (
              <>
                <Zahlplan client={client}
                  bezug={{ art: "order", customerId: kundeId, vorgangId: auftrag.id }}
                  idPraefix={`kauf-lese-${auftrag.id}`} nurLesend onLoaded={loaded.onRatesLoaded} />
                <GeplanteFaelligkeiten client={client} customerId={kundeId}
                  orderId={auftrag.id} currency={auftrag.currency}
                  neuBerechnenErlaubt={auftrag.status === "ACTIVE"} />
              </>
            )}
          </Vorgangsabschnitt>
          <Vorgangsabschnitt art="messstelle" titel={SECTION_TEXT.billingSources} faltung={readSections.billingSources}
            inhaltId={`${quellenPraefix}-inhalt`} {...counterProp(sourceCount)}>
            {billingSourcesBox(readSections.billingSources.offen)}
          </Vorgangsabschnitt>
          <Vorgangsabschnitt art="abrechnung" titel={SECTION_TEXT.billingVolume} faltung={readSections.billingVolume}
            inhaltId={`kauf-abrechnung-inhalt-${auftrag.id}`}>
            {billingBoxes(readSections.billingVolume.offen)}
          </Vorgangsabschnitt>
          <Vorgangsabschnitt art="textbaustein" titel={SECTION_TEXT.textAttachments}
            faltung={readSections.textAttachments} inhaltId={`kauf-anhaenge-inhalt-${auftrag.id}`}
            {...counterProp(loaded.attachmentCount)}>
            {attachmentsBox(readSections.textAttachments.offen, `kauf-lese-${auftrag.id}`)}
          </Vorgangsabschnitt>
          <FilesReadSection faltung={readSections.files} inhaltId={`kauf-dateien-inhalt-${auftrag.id}`}
            content={filesContent(files, readSections.files.offen, loaded)} />
        </>
      )}

      {vorschauOffen && (
        <Belegvorschau
          titel={`Auftragsbestätigung ${auftrag.displayNumber} · Vorschau`}
          // Die FASSUNG: die Version, dieselbe Grösse wie am Angebot (Ruling K6).
          fassung={String(auftrag.version)}
          laden={() => client.vorschauOrder(kundeId, auftrag.id)}
          // Der PDF-Knopf der Vorschau geht den BESTEHENDEN Download-Weg — kein zweiter
          // Weg zum selben Ziel (Spec, wörtlich).
          aufPdf={() => { void alsPdf(); }}
          onSchliessen={() => setVorschauOffen(false)} />
      )}
    </>
  );
}
